// THE METRICS REGISTRY (R-022) — the single source of truth for every number
// Reports puts on screen, in the CSV, and (Phase 5B) on a deck slide.
//
// Why this file exists: in v_20 the same measure was computed in three places —
// once in renderReports for the tile, once in exportReportCSV, once in
// exportReportPPT — and they drifted. A funder deck that disagrees with the
// screen it was exported from is worse than no deck. Here every metric is ONE
// definition: id → {label, caveat, unit, target, goodDirection, tier, section,
// dataState, compute}. The screen, the CSV and the deck all call compute();
// nothing recomputes a metric by hand anywhere else in the app.
//
// HONEST-METRICS RULES BAKED IN (R-002):
//  - `label` carries "(proxy)" whenever the measure APPROXIMATES the thing its
//    name suggests. The label is the promise; the caveat is the fine print.
//  - `caveat` is the exact definition shown in the ⓘ tooltip: numerator,
//    denominator, window, and the assumption that makes it approximate. It is
//    written to be readable by a funder, not by an engineer.
//  - `dataState: 'needs-field'` marks a metric the CURRENT schema cannot honestly
//    support. Its compute() returns value:null and the tile renders in the dashed
//    NEEDS FIELD state. It NEVER returns a plausible-looking number. This is the
//    whole point: a blank that says why beats a fabricated number that lies.
//  - `deltaSuppressed: true` marks metrics whose vs-previous-period comparison is
//    structurally meaningless (month-to-date counts). The screen omits the delta
//    rather than printing one that cannot be interpreted.
//
// EVERY compute() is PURE and total: 0 rows in → 0 or null out, never NaN,
// never Infinity, never a throw. Every division goes through safeDiv/pct.

import { STAGES, resolveStage, isClosedCase, ARCHIVE_REASONS, AE_SEVERITY, AE_OUTCOME } from '../db/contract.js';

// ─── Vocabulary ────────────────────────────────────────────────────────────

export const TIER = Object.freeze({ BOARD: 'board', ANALYST: 'analyst' });
export const DATA_STATE = Object.freeze({ LIVE: 'live', NEEDS_FIELD: 'needs-field' });
export const GOOD = Object.freeze({ UP: 'up', DOWN: 'down', NEUTRAL: 'neutral' });

// The report's sections, in screen order (mirrors the approved visual spec).
export const SECTIONS = Object.freeze([
  Object.freeze({ id: 'growth', title: 'Growth & enrolment', isNew: false }),
  Object.freeze({ id: 'sha', title: 'Access & SHA financing', isNew: true }),
  Object.freeze({ id: 'adherence', title: 'Adherence & persistence', isNew: true }),
  Object.freeze({ id: 'retention', title: 'Retention & outcomes', isNew: true }),
  Object.freeze({ id: 'safety', title: 'Safety · pharmacovigilance', isNew: false }),
  Object.freeze({ id: 'reach', title: 'Reachability & engagement', isNew: true }),
  Object.freeze({ id: 'finance', title: 'Financing & cost to serve', isNew: true }),
  Object.freeze({ id: 'geo', title: 'Geography & equity', isNew: true }),
  Object.freeze({ id: 'team', title: 'Team & workload', isNew: false }),
  Object.freeze({ id: 'dq', title: 'Data quality & governance', isNew: true }),
]);

// REPORT AREAS (the six-tab pivot) — the navigable grouping that replaced the
// single 10-section scroll. This is the SINGLE SOURCE OF TRUTH for which section
// lives under which tab: the view builds its pivot from it, the router validates
// a #/reports/<area> deep-link against it, and the total/disjoint test asserts
// directly on it. Overview owns NO section (it is the landing: exec narrative +
// KPI band + one scorecard per area). Every other section id appears in exactly
// one area — that invariant is what areaOfSection() and the mapping tests defend.
// Order is the patient journey, left to right; changing membership is a product
// decision made here, not in the view.
export const REPORT_AREAS = Object.freeze([
  Object.freeze({ key: 'overview', title: 'Overview', sections: Object.freeze([]) }),
  Object.freeze({ key: 'enrolment', title: 'Enrolment', sections: Object.freeze(['growth']) }),
  Object.freeze({ key: 'access', title: 'Access & financing', sections: Object.freeze(['sha']) }),
  Object.freeze({ key: 'treatment', title: 'Treatment & safety', sections: Object.freeze(['adherence', 'safety']) }),
  Object.freeze({ key: 'retention', title: 'Retention & reach', sections: Object.freeze(['retention', 'reach', 'geo']) }),
  Object.freeze({ key: 'operations', title: 'Operations', sections: Object.freeze(['finance', 'team', 'dq']) }),
]);

/** The area keys in tab order (overview first). Frozen. @type {ReadonlyArray<string>} */
export const REPORT_AREA_KEYS = Object.freeze(REPORT_AREAS.map((a) => a.key));

// Reverse index built once: section id → owning area key. A section that ever
// appeared under two areas would overwrite here and the disjoint test would
// catch the drift, but membership is authored disjoint above.
/** @type {Record<string, string>} */
const SECTION_TO_AREA = {};
for (const a of REPORT_AREAS) for (const s of a.sections) SECTION_TO_AREA[s] = a.key;

/**
 * The area key that owns a section, or undefined for an unmapped section.
 * A metric is reachable in its home area because its section maps here.
 * @param {string} sectionId
 * @returns {string|undefined}
 */
export function areaOfSection(sectionId) {
  return SECTION_TO_AREA[sectionId];
}

// PROGRAMME TARGETS — policy, not measurement. These are the targets the
// approved reports spec commits to; they are constants in ONE place so a tile,
// a CSV row and a deck slide can never quote different ones. Changing a target
// is a programme decision: change it here, with a note in the release message.
export const TARGETS = Object.freeze({
  REFILL_ON_TIME_PCT: 85, // % of refill gaps within the grace window
  TIME_TO_TREATMENT_DAYS: 7, // median consent → first cycle
  SHA_TAT_DAYS: 5, // median SHA application → approval
  LTFU_PCT: 7, // ceiling, not a goal
  CONSENT_REVIEW_DAYS: 2, // pending consent age before breach
  SHA_DECISION_DAYS: 5, // submitted SHA application age before breach
});

// A medication_cycles.cycle_status counts as "SHA cleared" at any of these —
// exact legacy list (v20 shaOK), kept here so tile + CSV + deck agree.
export const CYCLE_SHA_OK = Object.freeze(['approved', 'dispatched', 'received', 'active', 'reviewed']);

// Refill punctuality grace window: consecutive cycles ≤ 35 days apart count as
// on time. This is the PROXY — see the metric's caveat.
export const REFILL_GRACE_DAYS = 35;

// Urban/rural split (legacy RPT_URBAN): the 5 predominantly-urban counties;
// everything else counts as rural. Deliberately simple and stated on the tile.
export const URBAN_COUNTIES = Object.freeze(['nairobi', 'mombasa', 'kisumu', 'nakuru', 'kiambu']);

// Archive reasons that mean "we lost contact with this patient" (LTFU numerator).
export const LTFU_REASONS = Object.freeze(['lost_to_follow_up', 'unreachable_extended']);

const MONTHS = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);

// ─── Pure numeric helpers (all divide-by-zero guarded) ─────────────────────

/**
 * Divide, returning `fallback` (default null) when the denominator is 0/absent
 * or either side is not finite. NOTHING in this file divides without this.
 * @param {number} n numerator
 * @param {number} d denominator
 * @param {number|null} [fallback]
 * @returns {number|null}
 */
export function safeDiv(n, d, fallback = null) {
  if (!isFinite(n) || !isFinite(d) || !d) return fallback;
  const r = n / d;
  return isFinite(r) ? r : fallback;
}

/**
 * Whole-number percentage n/d, or null when d is 0 (never NaN on screen).
 * @param {number} n @param {number} d @returns {number|null}
 */
export function pct(n, d) {
  const r = safeDiv(n, d);
  return r === null ? null : Math.round(r * 100);
}

/**
 * One-decimal percentage (used where a 0.1pp move is meaningful, e.g. LTFU).
 * @param {number} n @param {number} d @returns {number|null}
 */
export function pct1(n, d) {
  const r = safeDiv(n, d);
  return r === null ? null : Math.round(r * 1000) / 10;
}

/**
 * Median of a numeric list; null for an empty list. Non-mutating.
 * @param {number[]} list @returns {number|null}
 */
export function median(list) {
  const a = (list || []).filter((x) => typeof x === 'number' && isFinite(x)).sort((x, y) => x - y);
  if (!a.length) return null;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
}

/**
 * Whole days from a → b (may be fractional). NaN-safe: returns null when either
 * timestamp is missing or unparseable.
 * @param {any} a @param {any} b @returns {number|null}
 */
export function daysBetween(a, b) {
  const ta = a ? new Date(a).getTime() : NaN;
  const tb = b ? new Date(b).getTime() : NaN;
  if (isNaN(ta) || isNaN(tb)) return null;
  return (tb - ta) / 86400000;
}

/**
 * 'YYYY-MM' bucket key for a timestamp; null when unparseable.
 * @param {any} d @returns {string|null}
 */
export function monthKey(d) {
  const t = d ? new Date(d) : null;
  if (!t || isNaN(t.getTime())) return null;
  return t.getFullYear() + '-' + String(t.getMonth() + 1).padStart(2, '0');
}

/**
 * 'Jan 26' label for a 'YYYY-MM' key. Returns the key unchanged if malformed.
 * @param {string} k @returns {string}
 */
export function monthLabel(k) {
  const p = String(k || '').split('-');
  if (p.length !== 2) return String(k || '');
  const i = Number(p[1]) - 1;
  return (MONTHS[i] || p[1]) + ' ' + p[0].slice(2);
}

/**
 * The last `n` month keys ending at `now` (inclusive), oldest first. Built from
 * local date parts so it never slips a month across a timezone boundary.
 * @param {number} n @param {Date} now @returns {string[]}
 */
export function lastMonthKeys(n, now) {
  const base = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    out.push(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'));
  }
  return out;
}

/**
 * Count rows per month key over the last `n` months (0-filled) — the sparkline
 * series. `field` names the timestamp column to bucket on.
 * @param {any[]} rows @param {number} n @param {Date} now @param {string} [field]
 * @returns {number[]}
 */
export function monthlySeries(rows, n, now, field = 'created_at') {
  /** @type {Record<string, number>} */
  const counts = {};
  for (const r of rows || []) {
    const k = r && monthKey(r[field]);
    if (k) counts[k] = (counts[k] || 0) + 1;
  }
  return lastMonthKeys(n, now).map((k) => counts[k] || 0);
}

/** Start of the current calendar month, local time. @param {Date} now @returns {Date} */
export function monthStart(now) {
  const b = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  return new Date(b.getFullYear(), b.getMonth(), 1);
}

/**
 * Descending {label, value} ranking of a string dimension over rows.
 * Blank/absent values are skipped (they are a data-quality fact, not a category).
 * @param {any[]} rows @param {(r: any) => any} get @param {number} [limit]
 * @returns {Array<{l: string, v: number}>}
 */
export function rankBy(rows, get, limit) {
  /** @type {Record<string, number>} */
  const counts = {};
  for (const r of rows || []) {
    const raw = get(r);
    const k = String(raw === null || raw === undefined ? '' : raw).trim();
    if (!k) continue;
    counts[k] = (counts[k] || 0) + 1;
  }
  const out = Object.keys(counts)
    .map((l) => ({ l, v: counts[l] }))
    .sort((a, b) => b.v - a.v || a.l.localeCompare(b.l));
  return typeof limit === 'number' ? out.slice(0, limit) : out;
}

/**
 * Age band from a date of birth. '—' when dob is absent or unparseable, which is
 * a real, selectable bucket meaning "not recorded" — never a silent drop.
 * Lives here (not in data/reports.js) because BOTH the slice chips and the equity
 * tiles band ages: two copies would drift the day a boundary moved (R-022).
 * @param {any} dob @param {Date} [now] @returns {string}
 */
export function ageBand(dob, now) {
  const d = dob ? new Date(dob) : null;
  if (!d || isNaN(d.getTime())) return '—';
  const base = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  let age = base.getFullYear() - d.getFullYear();
  const m = base.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && base.getDate() < d.getDate())) age--;
  if (!isFinite(age) || age < 0 || age > 120) return '—';
  if (age < 18) return 'Under 18';
  if (age < 30) return '18–29';
  if (age < 45) return '30–44';
  if (age < 60) return '45–59';
  if (age < 75) return '60–74';
  return '75+';
}

/**
 * Title-case a coded enum for display: 'not_recovered' → 'Not recovered'.
 * Returns '' for blank input so callers can decide the "not recorded" wording.
 * @param {any} v @returns {string}
 */
export function humanise(v) {
  const s = String(v === null || v === undefined ? '' : v).trim().replace(/_/g, ' ');
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Distribution of a coded field over rows, in a FIXED display order, with any
 * unrecognised value folded into a trailing 'Other' bucket and blanks counted
 * separately. Ordered vocabularies (severity, outcome) must not re-rank
 * themselves by volume — a severity chart that puts 'severe' first one month and
 * last the next is unreadable.
 * @param {any[]} rows @param {string} field @param {ReadonlyArray<{v: string, l: string, c?: string}>} vocab
 * @returns {{items: Array<{l: string, v: number, c?: string}>, total: number, missing: number}}
 */
export function orderedMix(rows, field, vocab) {
  /** @type {Record<string, number>} */
  const counts = {};
  let total = 0;
  let missing = 0;
  let other = 0;
  const known = new Set(vocab.map((x) => x.v));
  for (const r of rows || []) {
    const raw = r ? r[field] : null;
    const k = String(raw === null || raw === undefined ? '' : raw).trim().toLowerCase();
    total++;
    if (!k) { missing++; continue; }
    if (known.has(k)) counts[k] = (counts[k] || 0) + 1;
    else other++;
  }
  const items = vocab.filter((x) => counts[x.v]).map((x) => ({ l: x.l, v: counts[x.v], c: x.c }));
  if (other) items.push({ l: 'Other', v: other, c: 'var(--ink-muted)' });
  return { items, total, missing };
}

// Display vocabularies for the coded AE enums. The VALUES are taken from
// db/contract.js — the same lists the AE capture form renders — so the report
// cannot silently disagree with what the nurses were allowed to pick. Writing
// them out by hand here is how a FATAL adverse event ends up counted in a grey
// "Other" bar, which is the worst failure a safety report can have.
// Only the short label and the tone are chosen here; the test sweep asserts that
// every contract value is covered, so adding one to the form fails the build
// until it is given a home on the chart.
const AE_SEVERITY_TONE = Object.freeze({
  mild: 'var(--ok-700)',
  moderate: 'var(--warn-500)',
  severe: 'var(--warn-700)',
  life_threatening: 'var(--danger-700)',
  fatal: 'var(--danger-700)',
});

// Contract order is already clinical escalation (mild → fatal), so it is kept.
export const AE_SEVERITY_VOCAB = Object.freeze(
  AE_SEVERITY.map((s) => Object.freeze({ v: s.v, l: s.l, c: AE_SEVERITY_TONE[s.v] || 'var(--ink-muted)' }))
);

// Severity grades that count as "severe or worse" for the headline number. A
// fatal event must never be excluded from the figure a board reads first.
export const AE_SEVERE_OR_WORSE = Object.freeze(['severe', 'life_threatening', 'fatal']);

// Outcome is re-ordered best → worst (the contract lists it capture-order) and
// given short labels, because these are bar captions, not form options.
const AE_OUTCOME_DISPLAY = Object.freeze({
  recovered: { o: 1, l: 'Recovered', c: 'var(--ok-700)' },
  recovering: { o: 2, l: 'Recovering', c: 'var(--brand-700)' },
  recovered_with_sequelae: { o: 3, l: 'Recovered, with lasting effects', c: 'var(--warn-700)' },
  not_recovered: { o: 4, l: 'Not recovered', c: 'var(--danger-700)' },
  fatal: { o: 5, l: 'Fatal', c: 'var(--danger-700)' },
  unknown: { o: 6, l: 'Unknown', c: 'var(--ink-muted)' },
});

export const AE_OUTCOME_VOCAB = Object.freeze(
  AE_OUTCOME.map((o) => {
    const d = AE_OUTCOME_DISPLAY[o.v] || { o: 99, l: o.l, c: 'var(--ink-muted)' };
    return Object.freeze({ v: o.v, l: d.l, c: d.c, o: d.o });
  })
    .slice()
    .sort((a, b) => a.o - b.o)
);

// A dispense counts as "supplied" for adherence when we know how many days it
// covers. Anything else cannot enter an MPR numerator without inventing a number.
export const MPR_BANDS = Object.freeze([
  Object.freeze({ min: 95, l: 'Optimal (≥95%)', c: 'var(--ok-700)' }),
  Object.freeze({ min: 80, l: 'Adequate (80–94%)', c: 'var(--brand-700)' }),
  Object.freeze({ min: 50, l: 'Partial (50–79%)', c: 'var(--warn-700)' }),
  Object.freeze({ min: 0, l: 'Poor (<50%)', c: 'var(--danger-700)' }),
]);

/**
 * Medication possession ratio per case: days of medicine supplied ÷ days the
 * patient was expected to be covered, as a 0–100 number capped at 100.
 *
 * Window = first dispense → last dispense + that last dispense's days-supply, so
 * a patient who is still inside their most recent supply is not punished for not
 * having collected the next one yet. Cases with fewer than two dated dispenses,
 * or with no expected_days_supply recorded, are EXCLUDED (not scored zero) —
 * a missing field is a data-quality fact, not poor adherence.
 * @param {any[]} cycles @returns {Array<{case_id: string, mpr: number}>}
 */
export function mprByCase(cycles) {
  const per = cycleChains(cycles);
  const out = [];
  for (const id of Object.keys(per)) {
    const arr = per[id];
    if (arr.length < 2) continue;
    let supplied = 0;
    let known = 0;
    for (const y of arr) {
      const d = Number(y && y.expected_days_supply);
      if (isFinite(d) && d > 0) { supplied += d; known++; }
    }
    if (!known) continue;
    const lastSupply = Number(arr[arr.length - 1].expected_days_supply);
    const tail = isFinite(lastSupply) && lastSupply > 0 ? lastSupply : 0;
    const span = daysBetween(arr[0].created_at, arr[arr.length - 1].created_at);
    if (span === null || span < 0) continue;
    const windowDays = span + tail;
    const r = safeDiv(supplied * 100, windowDays);
    if (r === null) continue;
    out.push({ case_id: id, mpr: Math.min(100, Math.round(r)) });
  }
  return out;
}

// ─── Row-shape predicates (one definition each) ────────────────────────────

/**
 * Active membership: program_status 'active' OR absent (legacy treated a null
 * program_status as active — the column was added after the earliest rows).
 * @param {any} c @returns {boolean}
 */
export function isActiveCase(c) {
  if (!c) return false;
  // ACTIVE IS THE COMPLEMENT OF CLOSED. Testing program_status alone made the
  // two predicates disagree: a case with no program_status but a closed_* stage
  // or an archived_at was counted as active AND as closed, so the 'active' tile
  // could report more heads than it had cases. isClosedCase is the shared
  // classifier every other surface uses; active must be defined against it.
  if (isClosedCase(c)) return false;
  const s = c.program_status;
  return s === 'active' || s === null || s === undefined || s === '';
}

/**
 * "On treatment": the case has reached at least one dispensing cycle
 * (active_cycle_no ≥ 1) or sits in the On-Therapy stage. PROXY — there is no
 * treatment_started_at column; see the metric caveat.
 * @param {any} c @returns {boolean}
 */
export function isOnTreatment(c) {
  if (!c) return false;
  return Number(c.active_cycle_no || 0) >= 1 || resolveStage(c) === 6;
}

/**
 * Contact permission: the patient consented to at least one channel. This is a
 * PERMISSION flag on the consent form, not a verified reachable number.
 * @param {any} c @returns {boolean}
 */
export function hasCommsConsent(c) {
  const cc = (c && c.comms_consent) || {};
  return ['call', 'sms', 'whatsapp', 'email'].some((k) => cc[k] === true);
}

/**
 * Per-case chronological cycle chains (case_id → cycles sorted by created_at).
 * Rows without a case_id or a timestamp are dropped — they cannot join a chain.
 * @param {any[]} cycles @returns {Record<string, any[]>}
 */
export function cycleChains(cycles) {
  /** @type {Record<string, any[]>} */
  const per = {};
  for (const y of cycles || []) {
    if (!y || !y.case_id || !y.created_at) continue;
    (per[y.case_id] || (per[y.case_id] = [])).push(y);
  }
  for (const id of Object.keys(per)) {
    per[id].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }
  return per;
}

/**
 * Refill-gap punctuality over every consecutive cycle pair: {total, onTime}.
 * A gap ≤ REFILL_GRACE_DAYS counts as on time. Cases with one cycle contribute
 * no gaps (correctly — there is nothing to be late for yet).
 * @param {any[]} cycles @returns {{total: number, onTime: number}}
 */
export function refillGaps(cycles) {
  const per = cycleChains(cycles);
  let total = 0;
  let onTime = 0;
  for (const id of Object.keys(per)) {
    const arr = per[id];
    for (let i = 1; i < arr.length; i++) {
      const g = daysBetween(arr[i - 1].created_at, arr[i].created_at);
      if (g === null) continue;
      total++;
      if (g <= REFILL_GRACE_DAYS) onTime++;
    }
  }
  return { total, onTime };
}

/**
 * Days from enrolment (case created_at) to the FIRST medication cycle, per case.
 * Values outside [0, 365) are discarded as data errors (a negative gap means the
 * cycle predates the case row; >1 year is a migration artefact, not a wait).
 * @param {any[]} cases @param {any[]} cycles @returns {number[]}
 */
export function timeToTreatmentDays(cases, cycles) {
  const per = cycleChains(cycles);
  /** @type {Record<string, any>} */
  const byId = {};
  for (const c of cases || []) if (c && c.case_id) byId[c.case_id] = c;
  const out = [];
  for (const id of Object.keys(per)) {
    const c = byId[id];
    if (!c || !c.created_at) continue;
    const d = daysBetween(c.created_at, per[id][0].created_at);
    if (d === null || d < 0 || d >= 365) continue;
    out.push(d);
  }
  return out;
}

/**
 * SHA turnaround days (application → approval) per case, bucketed by the month
 * the approval landed. Same [0,365) sanity window as above.
 * @param {any[]} cases @returns {Record<string, number[]>}
 */
export function shaTatByMonth(cases) {
  /** @type {Record<string, number[]>} */
  const byM = {};
  for (const c of cases || []) {
    if (!c || !c.sha_application_date || !c.sha_approval_date) continue;
    const d = daysBetween(c.sha_application_date, c.sha_approval_date);
    if (d === null || d < 0 || d >= 365) continue;
    const k = monthKey(c.sha_approval_date);
    if (!k) continue;
    (byM[k] || (byM[k] = [])).push(d);
  }
  return byM;
}

/**
 * Sum an amount column defensively — non-numeric amounts contribute 0 rather
 * than poisoning the total with NaN (a single bad row must not blank the tile).
 * @param {any[]} rows @param {string} [field] @returns {number}
 */
export function sumAmount(rows, field = 'amount') {
  let t = 0;
  for (const r of rows || []) {
    const n = Number((r && r[field]) || 0);
    if (isFinite(n)) t += n;
  }
  return t;
}

// ─── Display formatters (the ONE place a value becomes a string) ───────────

/** Thousands-separated integer, or '—' for null. @param {number|null} v @returns {string} */
export function fmtNum(v) {
  if (v === null || v === undefined || !isFinite(Number(v))) return '—';
  return Math.round(Number(v)).toLocaleString('en-KE');
}

/** '78%' or '—'. @param {number|null} v @returns {string} */
export function fmtPct(v) {
  return v === null || v === undefined || !isFinite(Number(v)) ? '—' : Number(v) + '%';
}

/** '9 d' or '—'. @param {number|null} v @returns {string} */
export function fmtDays(v) {
  return v === null || v === undefined || !isFinite(Number(v)) ? '—' : Math.round(Number(v)) + ' d';
}

/** 'KSh 3,410' / 'KSh 3.15M' or '—'. @param {number|null} v @returns {string} */
export function fmtMoney(v) {
  if (v === null || v === undefined || !isFinite(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return 'KSh ' + (n / 1e6).toFixed(2) + 'M';
  return 'KSh ' + Math.round(n).toLocaleString('en-KE');
}

// A compute() result. `value` is the comparable number (delta arithmetic runs on
// it); `display` is what the tile prints; `data` carries structured payload for
// chart/table tiles; `n` is the sample size behind a median/percentage.
/**
 * @typedef {Object} MetricResult
 * @property {number|null} value
 * @property {string} display
 * @property {string} [sub]
 * @property {number} [n]
 * @property {any} [data]
 */

/** Every compute() ends here for a "nothing to show" outcome. @param {string} [sub] @returns {MetricResult} */
function none(sub) {
  return { value: null, display: '—', sub: sub || '' };
}

/**
 * The standard result for a needs-field metric: NO number, ever. Callers render
 * the dashed NEEDS FIELD state off dataState, but compute() is defensive too so
 * a mis-wired caller still cannot print a fabricated value.
 * @returns {MetricResult}
 */
function needsField() {
  return { value: null, display: '—', sub: 'needs a field the schema does not have yet' };
}

// ─── THE REGISTRY ──────────────────────────────────────────────────────────
// Ordered: the 12 KPI-band metrics first (kpi:true), then the section tiles in
// section order. `compute(rows, ctx)`:
//   rows = {cases, cycles, consents, aes, expenses, pd}  (already filtered)
//   ctx  = {now: Date, window: {a,b}|null, programmes: Array}

/** @type {ReadonlyArray<any>} */
export const METRICS = Object.freeze([
  // ═══ KPI band ═══════════════════════════════════════════════════════════
  {
    id: 'enrolled',
    label: 'Total enrolled',
    caveat:
      'Numerator: every case row in the current view (all statuses, including closed and archived). No denominator. Window: the selected period applied to the chosen clock.',
    unit: 'patients',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'growth',
    kpi: true,
    dataState: DATA_STATE.LIVE,
    sparkOf: 'cases',
    compute(rows, ctx) {
      const cases = (rows && rows.cases) || [];
      const year = (ctx && ctx.now ? ctx.now : new Date()).getFullYear();
      const thisYear = cases.filter((c) => {
        const t = c && c.created_at ? new Date(c.created_at) : null;
        return t && !isNaN(t.getTime()) && t.getFullYear() === year;
      }).length;
      return { value: cases.length, display: fmtNum(cases.length), sub: year + ': ' + fmtNum(thisYear) };
    },
  },
  {
    id: 'active',
    label: 'Active patients',
    caveat:
      "Numerator: cases whose program_status is 'active' (or blank — the column post-dates the earliest rows, and blank has always meant active). Closed, archived and deceased cases are excluded.",
    unit: 'patients',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'growth',
    kpi: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      const active = cases.filter(isActiveCase).length;
      // The shared classifier, not a bare program_status test: isClosedCase also
      // recognises 'archived'/'deceased', a closed_* current_stage and a bare
      // archived_at. The headline `value` already used it (via isActiveCase), so a
      // narrower predicate here made the caption disagree with the number above it.
      const closed = cases.filter(isClosedCase).length;
      return { value: active, display: fmtNum(active), sub: fmtNum(closed) + ' closed / archived' };
    },
  },
  {
    id: 'on_treatment',
    label: 'On treatment (proxy)',
    caveat:
      'PROXY. Numerator: active cases that have reached at least one dispensing cycle (active_cycle_no ≥ 1) or sit in the On-Therapy stage. Denominator: active cases. There is no treatment_start date in the schema, so a patient dispensed but not yet stage-moved is counted from the cycle counter.',
    unit: 'patients',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'growth',
    kpi: true,
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      const active = cases.filter(isActiveCase);
      const n = active.filter(isOnTreatment).length;
      const share = pct(n, active.length);
      return { value: n, display: fmtNum(n), sub: share === null ? '' : share + '% of active', n: active.length };
    },
  },
  {
    id: 'refills',
    label: 'Total refills (proxy)',
    caveat:
      'PROXY. Numerator: medication_cycles rows in the current view. One cycle row stands for one dispensing/refill event — the schema has no separate dispense ledger, so a cycle opened but never collected still counts here.',
    unit: 'cycles',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'adherence',
    kpi: true,
    dataState: DATA_STATE.LIVE,
    sparkOf: 'cycles',
    compute(rows, ctx) {
      const cycles = (rows && rows.cycles) || [];
      const year = (ctx && ctx.now ? ctx.now : new Date()).getFullYear();
      const thisYear = cycles.filter((c) => {
        const t = c && c.created_at ? new Date(c.created_at) : null;
        return t && !isNaN(t.getTime()) && t.getFullYear() === year;
      }).length;
      return { value: cycles.length, display: fmtNum(cycles.length), sub: year + ': ' + fmtNum(thisYear) };
    },
  },
  {
    id: 'sha_approved',
    label: 'SHA approvals',
    caveat:
      'Numerator: medication_cycles whose cycle_status has cleared SHA (approved, dispatched, received, active or reviewed). Counted per cycle, not per patient — one patient contributes one approval per cycle.',
    unit: 'cycles',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'sha',
    kpi: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const cycles = (rows && rows.cycles) || [];
      const okRows = cycles.filter((c) => CYCLE_SHA_OK.includes(c && c.cycle_status));
      const year = (ctx && ctx.now ? ctx.now : new Date()).getFullYear();
      const thisYear = okRows.filter((c) => {
        const t = c.created_at ? new Date(c.created_at) : null;
        return t && !isNaN(t.getTime()) && t.getFullYear() === year;
      }).length;
      return { value: okRows.length, display: fmtNum(okRows.length), sub: year + ': ' + fmtNum(thisYear) };
    },
  },
  {
    id: 'sha_pending',
    label: 'Pending SHA',
    caveat:
      "Numerator: medication_cycles still at cycle_status 'pending' — a claim submitted and awaiting a decision. Lower is better; a rising count means the queue is growing faster than SHA clears it.",
    unit: 'cycles',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    kpi: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cycles = (rows && rows.cycles) || [];
      const n = cycles.filter((c) => (c && c.cycle_status) === 'pending').length;
      return { value: n, display: fmtNum(n), sub: n === 1 ? '1 claim awaiting a decision' : fmtNum(n) + ' claims awaiting a decision' };
    },
  },
  {
    id: 'refills_on_time',
    label: 'Refills on time (proxy)',
    caveat:
      'PROXY. Numerator: consecutive medication-cycle pairs no more than ' +
      REFILL_GRACE_DAYS +
      ' days apart. Denominator: all consecutive cycle pairs in view. The schema stores no per-cycle due date, so the gap between dispensing dates stands in for punctuality — a patient collecting early and late in turn can still read as on time.',
    unit: '%',
    target: TARGETS.REFILL_ON_TIME_PCT,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'adherence',
    kpi: true,
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const g = refillGaps((rows && rows.cycles) || []);
      const v = pct(g.onTime, g.total);
      return {
        value: v,
        display: fmtPct(v),
        sub: g.total === 1 ? '1 refill gap' : fmtNum(g.total) + ' refill gaps',
        n: g.total,
      };
    },
  },
  {
    id: 'contactable',
    label: 'Contactable (proxy)',
    caveat:
      'PROXY. Numerator: cases where the patient consented to at least one contact channel (call, SMS, WhatsApp or email). Denominator: all cases in view. This measures PERMISSION recorded on the consent form, not a verified working phone number — a patient may be contactable on paper and unreachable in practice.',
    unit: '%',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'reach',
    kpi: true,
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      const n = cases.filter(hasCommsConsent).length;
      const v = pct(n, cases.length);
      return { value: v, display: fmtPct(v), sub: 'of ' + fmtNum(cases.length) + ' patients', n: cases.length };
    },
  },
  {
    id: 'time_to_treatment',
    label: 'Time to treatment (median)',
    caveat:
      'Median days from case creation to the first medication cycle, over cases in view that have at least one cycle. Gaps below 0 or at/above 365 days are discarded as data errors, not waits. Cases still waiting for a first cycle are NOT in the denominator — this is the time taken by those who started, not the time patients are waiting.',
    unit: 'days',
    target: TARGETS.TIME_TO_TREATMENT_DAYS,
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    kpi: true,
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const days = timeToTreatmentDays((rows && rows.cases) || [], (rows && rows.cycles) || []);
      const m = median(days);
      if (m === null) return none('needs first cycles');
      return { value: m, display: fmtDays(m), sub: 'median · n=' + fmtNum(days.length), n: days.length };
    },
  },
  {
    id: 'ltfu',
    label: 'Lost to follow-up',
    caveat:
      "Numerator: cases closed with archive_reason_code 'lost_to_follow_up' or 'unreachable_extended'. Denominator: all cases in view. Only CLOSED cases carry a reason code, so a patient who has gone quiet but is not yet closed is not counted here — this is a lagging measure.",
    unit: '%',
    target: TARGETS.LTFU_PCT,
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'retention',
    kpi: true,
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      const n = cases.filter((c) => LTFU_REASONS.includes(c && c.archive_reason_code)).length;
      const v = pct1(n, cases.length);
      return { value: v, display: v === null ? '—' : v + '%', sub: n === 1 ? '1 patient' : fmtNum(n) + ' patients', n: cases.length };
    },
  },
  {
    id: 'cost_per_patient',
    label: 'Cost per active patient',
    caveat:
      'Numerator: total pass-through expenses (expenses.amount, excluding soft-deleted rows) in the current view. Denominator: active cases in view. Pass-through spend only — it excludes staff time and overheads, so it is a cost-to-serve floor, not a full unit cost.',
    unit: 'KSh',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'finance',
    kpi: true,
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const total = sumAmount((rows && rows.expenses) || []);
      const active = ((rows && rows.cases) || []).filter(isActiveCase).length;
      const v = safeDiv(total, active);
      if (v === null) return none('no active patients in view');
      return { value: Math.round(v), display: fmtMoney(v), sub: fmtMoney(total) + ' over ' + fmtNum(active), n: active };
    },
  },
  {
    id: 'serious_ae_mtd',
    label: 'Serious AEs (month to date)',
    caveat:
      'Count of adverse_events marked serious with a created_at in the CURRENT calendar month, regardless of the selected period. Always month-to-date, so it answers "what is happening right now" — which is why it carries no previous-period comparison.',
    unit: 'events',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'safety',
    kpi: true,
    // The previous window's aggregate is NOT the previous month's serious-AE
    // count (on the AE clock it is structurally zero), so a delta here would be
    // an artefact. R-002: suppress rather than mislead.
    deltaSuppressed: true,
    dataState: DATA_STATE.LIVE,
    sparkOf: 'seriousAes',
    compute(rows, ctx) {
      const ms = monthStart(ctx && ctx.now).getTime();
      // `aesAll` — the AE stream BEFORE the period/clock narrowing. The caveat
      // promises a current-month count "regardless of the selected period", and
      // that promise is what justifies deltaSuppressed; reading the narrowed
      // `aes` would drop a serious event reported this month on a patient who
      // enrolled outside the selected period, under-reporting a safety headline.
      const aes = (rows && (rows.aesAll || rows.aes)) || [];
      const n = aes.filter((a) => {
        if (!a || a.seriousness !== 'serious' || !a.created_at) return false;
        const t = new Date(a.created_at).getTime();
        return !isNaN(t) && t >= ms;
      }).length;
      return { value: n, display: fmtNum(n), sub: n ? 'review the safety register' : 'none reported' };
    },
  },

  // ═══ Section: Growth & enrolment ════════════════════════════════════════
  {
    id: 'enrolment_by_month',
    label: 'Monthly enrolment trend',
    caveat: 'Count of cases created per calendar month, over the months present in the current view. Bars are counts, not rates.',
    unit: 'patients/month',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'growth',
    chart: 'bar',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const c of (rows && rows.cases) || []) {
        const k = monthKey(c && c.created_at);
        if (k) by[k] = (by[k] || 0) + 1;
      }
      const keys = Object.keys(by).sort();
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: by[k] }));
      const total = series.reduce((a, s) => a + s.v, 0);
      return { value: total, display: fmtNum(total), sub: series.length + (series.length === 1 ? ' month' : ' months'), data: { series } };
    },
  },
  {
    id: 'consent_funnel',
    label: 'Consent funnel',
    caveat:
      'Consent submissions by review status. Submitted = every submission in view; Reviewed = anything no longer pending; Promoted = became a case. Consent rows carry no case link, so patient-level slices (county, nurse, stage) cannot narrow this card — only programme and the period do.',
    unit: 'submissions',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'growth',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cs = (rows && rows.consents) || [];
      const st = (v) => cs.filter((c) => String((c && c.status) || '').toLowerCase() === v).length;
      const submitted = cs.length;
      const pending = st('pending');
      const promoted = st('promoted');
      const declined = st('declined');
      const onHold = st('on_hold');
      const reviewed = submitted - pending;
      const conv = pct(promoted, submitted);
      return {
        value: conv,
        display: fmtPct(conv),
        sub: 'submitted → promoted',
        n: submitted,
        data: {
          steps: [
            { l: 'Submitted', v: submitted, c: 'var(--brand-700)' },
            { l: 'Reviewed', v: reviewed, c: 'var(--warn-700)' },
            { l: 'Promoted to case', v: promoted, c: 'var(--ok-700)' },
            { l: 'Declined', v: declined, c: 'var(--danger-700)' },
            { l: 'On hold', v: onHold, c: 'var(--ink-muted)' },
          ],
          conversion: conv,
        },
      };
    },
  },
  {
    id: 'pipeline_distribution',
    label: 'Pipeline distribution',
    caveat:
      'Active cases bucketed into the 8 board stages by current_stage (falling back to the free-text status for legacy rows). Closed and archived cases are excluded so the pipeline is not inflated.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'growth',
    chart: 'doughnut',
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const counts = STAGES.map((s) => ({ l: s.l, s: s.s, c: s.c, v: 0 }));
      let total = 0;
      for (const c of (rows && rows.cases) || []) {
        if (!isActiveCase(c) || isClosedCase(c)) continue;
        counts[resolveStage(c)].v++;
        total++;
      }
      return { value: total, display: fmtNum(total), sub: 'active in pipeline', data: { stages: counts } };
    },
  },
  {
    id: 'sla_board',
    label: 'SLA & compliance board',
    caveat:
      'Breach counts against the programme clocks that CAN be measured from loaded data: consent reviews still pending after ' +
      TARGETS.CONSENT_REVIEW_DAYS +
      ' days, and SHA applications submitted more than ' +
      TARGETS.SHA_DECISION_DAYS +
      ' days ago with no decision. Clocks that need the live work queue are listed as not measured here rather than shown as zero.',
    unit: 'breaches',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'growth',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const now = (ctx && ctx.now ? ctx.now : new Date()).getTime();
      const consents = (rows && rows.consents) || [];
      const cases = (rows && rows.cases) || [];
      const consentBreach = consents.filter((c) => {
        if (!c || !/^pending$/i.test(String(c.status || '')) || !c.created_at) return false;
        const t = new Date(c.created_at).getTime();
        return !isNaN(t) && now - t > TARGETS.CONSENT_REVIEW_DAYS * 86400000;
      }).length;
      const shaBreach = cases.filter((c) => {
        if (!c || String(c.sha_status || '').toLowerCase() !== 'submitted' || !c.sha_application_date) return false;
        const t = new Date(c.sha_application_date).getTime();
        return !isNaN(t) && now - t > TARGETS.SHA_DECISION_DAYS * 86400000;
      }).length;
      const list = [
        { l: 'Consent review ≤ ' + TARGETS.CONSENT_REVIEW_DAYS + ' d', n: consentBreach, t: 'Consent submissions still pending after ' + TARGETS.CONSENT_REVIEW_DAYS + ' days' },
        { l: 'SHA decision ≤ ' + TARGETS.SHA_DECISION_DAYS + ' d', n: shaBreach, t: 'Submitted SHA applications with no decision after ' + TARGETS.SHA_DECISION_DAYS + ' days' },
        { l: 'Work-queue items overdue', n: null, t: 'Not measured here — the work queue is loaded by the Today view, not by Reports' },
      ];
      const total = consentBreach + shaBreach;
      return { value: total, display: fmtNum(total), sub: total === 1 ? '1 breach' : fmtNum(total) + ' breaches', data: { rows: list } };
    },
  },
  {
    id: 'stage_ageing',
    label: 'Stage ageing',
    caveat:
      'How long each ACTIVE patient has sat in their current pipeline stage, from the most recent case_stage_history row for that case. Deliberately NOT cases.updated_at, which moves on any edit and would report "time since someone touched the record" instead of time in stage. A case with no history row yet (enrolled before stage tracking, or never moved) is counted as never-moved and reported separately rather than being given an invented age. Point-in-time: the period filter narrows WHICH patients are counted, not the clock.',
    unit: 'days',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'growth',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const now = (ctx && ctx.now instanceof Date && !isNaN(ctx.now.getTime()) ? ctx.now : new Date()).getTime();
      const act = ((rows && rows.cases) || []).filter(isActiveCase);
      if (!act.length) return none('no active patients in view');
      // Latest stage move per case.
      /** @type {Record<string, number>} */
      const lastMove = {};
      for (const h of (rows && rows.stageHistory) || []) {
        if (!h || !h.case_id || !h.created_at) continue;
        const t = new Date(h.created_at).getTime();
        if (!isFinite(t)) continue;
        if (!lastMove[h.case_id] || t > lastMove[h.case_id]) lastMove[h.case_id] = t;
      }
      /** @type {Record<string, {n: number, ages: number[]}>} */
      const byStage = {};
      let untracked = 0;
      const allAges = [];
      for (const c of act) {
        const t = lastMove[c.case_id];
        if (!t) { untracked++; continue; }
        const age = (now - t) / 86400000;
        if (!isFinite(age) || age < 0) { untracked++; continue; }
        const s = STAGES[resolveStage(c)];
        const key = (s && s.l) || 'Unknown';
        const cell = byStage[key] || (byStage[key] = { n: 0, ages: [] });
        cell.n++;
        cell.ages.push(age);
        allAges.push(age);
      }
      if (!allAges.length) return none(fmtNum(untracked) + ' active patients, none with a recorded stage move yet');
      const items = Object.keys(byStage)
        .map((l) => {
          const m = median(byStage[l].ages) || 0;
          return { l, v: Math.round(m), n: byStage[l].n, c: m > 30 ? 'var(--danger-700)' : m > 14 ? 'var(--warn-700)' : 'var(--ok-700)' };
        })
        .sort((a, b) => b.v - a.v);
      const overall = median(allAges);
      const stuck = allAges.filter((a) => a > 30).length;
      return {
        value: overall === null ? null : Math.round(overall),
        display: fmtDays(overall),
        sub:
          'median in stage · ' + fmtNum(stuck) + ' over 30 d' + (untracked ? ' · ' + fmtNum(untracked) + ' never moved' : ''),
        n: allAges.length,
        data: { items, untracked, stuck },
      };
    },
  },
  {
    id: 'programme_breakdown',
    label: 'Programme breakdown',
    caveat:
      'Straight from the v_programme_dashboard view — server-computed per-programme totals. NOTE: this view is not narrowed by the slice chips or the period; it always shows programme-wide current state.',
    unit: 'programmes',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.ANALYST,
    section: 'growth',
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const pd = (rows && rows.pd) || [];
      return { value: pd.length, display: fmtNum(pd.length), sub: pd.length === 1 ? 'programme' : 'programmes', data: { rows: pd } };
    },
  },

  {
    id: 'diagnosis_mix',
    label: 'Diagnosis mix',
    caveat:
      'Patients in view grouped by cases.diagnosis, top 8 shown. This is the clinical shape of the programme and the first thing a sponsor or an ethics committee asks for. Diagnosis is captured as free text, so spelling and abbreviation variants appear as separate rows — a long tail of near-duplicates is a coding problem to fix at intake, not a real spread of disease.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'growth',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      const ranked = rankBy(cases, (c) => c && c.diagnosis, 8);
      const known = ranked.reduce((a, r) => a + r.v, 0);
      if (!known) return none('no diagnosis recorded on any case in view');
      const missing = cases.length - rankBy(cases, (c) => c && c.diagnosis).reduce((a, r) => a + r.v, 0);
      return {
        value: ranked.length,
        display: fmtNum(ranked[0].v),
        sub: 'in ' + ranked[0].l + (missing > 0 ? ' · ' + fmtNum(missing) + ' unrecorded' : ''),
        n: known,
        data: { items: ranked.map((r) => ({ l: r.l, v: r.v, c: 'var(--brand-700)' })), missing },
      };
    },
  },
  {
    id: 'cancer_stage_mix',
    label: 'Cancer stage at enrolment',
    caveat:
      'Patients in view grouped by cases.cancer_stage as recorded at intake. Stage drives both expected time on therapy and expected outcome, so it is the single most important variable for reading every retention and cost number on this report — a programme skewed to late-stage disease will look worse on persistence for reasons that have nothing to do with its service. Cases with no stage recorded are counted separately.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'growth',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      if (!cases.length) return none('no cases in view');
      const ranked = rankBy(cases, (c) => humanise(c && c.cancer_stage), 8);
      const known = ranked.reduce((a, r) => a + r.v, 0);
      if (!known) return none('no cancer stage recorded on any case in view');
      const missing = cases.length - known;
      return {
        value: known,
        display: fmtNum(known),
        sub: 'staged of ' + fmtNum(cases.length) + (missing > 0 ? ' · ' + fmtNum(missing) + ' unstaged' : ''),
        n: cases.length,
        data: { items: ranked.map((r) => ({ l: r.l, v: r.v, c: 'var(--brand-700)' })), missing },
      };
    },
  },

  // ═══ Section: Access & SHA financing ════════════════════════════════════
  {
    id: 'sha_coverage_gap',
    label: 'SHA coverage gap',
    caveat:
      "Active cases whose sha_status is NOT approved or active, split by where they are stuck (not started / submitted and waiting / rejected). These are the patients whose cost the programme absorbs. Denominator: active cases in view.",
    unit: 'patients',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const act = ((rows && rows.cases) || []).filter(isActiveCase);
      const b = { not_started: 0, submitted: 0, rejected: 0 };
      for (const c of act) {
        const s = String((c && c.sha_status) || 'not_started').toLowerCase();
        if (s === 'approved' || s === 'active') continue;
        if (s === 'submitted') b.submitted++;
        else if (s === 'rejected') b.rejected++;
        else b.not_started++;
      }
      const gap = b.not_started + b.submitted + b.rejected;
      return {
        value: gap,
        display: fmtNum(gap),
        sub: 'of ' + fmtNum(act.length) + ' active not covered',
        n: act.length,
        data: {
          items: [
            { l: 'Not started', v: b.not_started, c: 'var(--warn-700)' },
            { l: 'Submitted (waiting)', v: b.submitted, c: 'var(--warn-500)' },
            { l: 'Rejected', v: b.rejected, c: 'var(--danger-700)' },
          ],
          activeTotal: act.length,
        },
      };
    },
  },
  {
    id: 'sha_tat_trend',
    label: 'SHA approval turnaround',
    caveat:
      'Median days from sha_application_date to sha_approval_date, bucketed by the month the approval landed. Only cases with BOTH dates recorded contribute; turnarounds below 0 or at/above 365 days are discarded as data errors. Target ' +
      TARGETS.SHA_TAT_DAYS +
      ' days.',
    unit: 'days',
    target: TARGETS.SHA_TAT_DAYS,
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    chart: 'line',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const byM = shaTatByMonth((rows && rows.cases) || []);
      const keys = Object.keys(byM).sort().slice(-12);
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: Math.round((median(byM[k]) || 0) * 10) / 10 }));
      const latest = series.length ? series[series.length - 1].v : null;
      const prev = series.length > 1 ? series[series.length - 2].v : null;
      const n = keys.reduce((a, k) => a + byM[k].length, 0);
      if (latest === null) return none('no approvals with both dates');
      return {
        value: latest,
        display: fmtDays(latest),
        sub: 'median, latest month · n=' + fmtNum(n),
        n,
        data: { series, latest, prev },
      };
    },
  },
  {
    id: 'sha_pending_ageing',
    label: 'Pending SHA ageing',
    caveat:
      'Cases at sha_status "submitted" bucketed by how long ago sha_application_date was. Cases without an application date cannot be aged and are excluded from the buckets.',
    unit: 'patients',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const now = (ctx && ctx.now ? ctx.now : new Date()).getTime();
      const b = [0, 0, 0, 0];
      let n = 0;
      for (const c of (rows && rows.cases) || []) {
        if (!c || String(c.sha_status || '').toLowerCase() !== 'submitted' || !c.sha_application_date) continue;
        const t = new Date(c.sha_application_date).getTime();
        if (isNaN(t)) continue;
        const d = (now - t) / 86400000;
        if (!(d >= 0)) continue;
        n++;
        if (d <= 3) b[0]++;
        else if (d <= 7) b[1]++;
        else if (d <= 14) b[2]++;
        else b[3]++;
      }
      return {
        value: n,
        display: fmtNum(n),
        sub: n === 1 ? '1 application awaiting a decision' : fmtNum(n) + ' applications awaiting a decision',
        data: {
          items: [
            { l: '0–3 days', v: b[0], c: 'var(--ok-700)' },
            { l: '4–7 days', v: b[1], c: 'var(--warn-700)' },
            { l: '8–14 days', v: b[2], c: 'var(--warn-500)' },
            { l: '15+ days', v: b[3], c: 'var(--danger-700)' },
          ],
          over15: b[3],
        },
      };
    },
  },
  {
    id: 'sha_approvals_by_month',
    label: 'SHA approvals per month',
    caveat: 'Medication cycles that cleared SHA, bucketed by the month the cycle row was created. Counts cycles, not patients.',
    unit: 'cycles/month',
    goodDirection: GOOD.UP,
    tier: TIER.ANALYST,
    section: 'sha',
    chart: 'line',
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const c of (rows && rows.cycles) || []) {
        if (!CYCLE_SHA_OK.includes(c && c.cycle_status)) continue;
        const k = monthKey(c.created_at);
        if (k) by[k] = (by[k] || 0) + 1;
      }
      const keys = Object.keys(by).sort();
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: by[k] }));
      const total = series.reduce((a, s) => a + s.v, 0);
      return { value: total, display: fmtNum(total), sub: series.length + (series.length === 1 ? ' month' : ' months'), data: { series } };
    },
  },
  {
    id: 'time_to_treatment_trend',
    label: 'Time to treatment by month',
    caveat:
      'Median days from case creation to first medication cycle, bucketed by the ENROLMENT month (so a cohort is judged on when it joined, not when it finally started). Same [0, 365) sanity window as the headline metric.',
    unit: 'days',
    target: TARGETS.TIME_TO_TREATMENT_DAYS,
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    chart: 'bar',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const per = cycleChains((rows && rows.cycles) || []);
      /** @type {Record<string, number[]>} */
      const byM = {};
      for (const c of (rows && rows.cases) || []) {
        if (!c || !c.case_id || !c.created_at || !per[c.case_id]) continue;
        const d = daysBetween(c.created_at, per[c.case_id][0].created_at);
        if (d === null || d < 0 || d >= 365) continue;
        const k = monthKey(c.created_at);
        if (!k) continue;
        (byM[k] || (byM[k] = [])).push(d);
      }
      const keys = Object.keys(byM).sort().slice(-12);
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: Math.round((median(byM[k]) || 0) * 10) / 10 }));
      const all = keys.reduce((a, k) => a.concat(byM[k]), /** @type {number[]} */ ([]));
      const m = median(all);
      if (m === null) return none('needs first cycles');
      return { value: m, display: fmtDays(m), sub: 'median · n=' + fmtNum(all.length), n: all.length, data: { series } };
    },
  },
  {
    id: 'sha_denial_reasons',
    label: 'Denial & rework reasons',
    caveat:
      'NEEDS FIELD. Why a claim bounced is not stored anywhere: medication_cycles records that a cycle was rejected, never the reason. Capturing it needs a denial_reason column on medication_cycles (or a coded rejection note). Until then this card stays blank rather than guessing from free text.',
    unit: 'claims',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'sha',
    dataState: DATA_STATE.NEEDS_FIELD,
    compute: () => needsField(),
  },

  // ═══ Section: Adherence & persistence ═══════════════════════════════════
  {
    id: 'adherence_bands',
    label: 'Adherence bands (MPR)',
    caveat:
      'Medication possession ratio per patient: days of medicine supplied (sum of medication_cycles.expected_days_supply) ÷ days the patient was expected to be covered, from the first dispense to the last dispense plus that last supply. Capped at 100% so stockpiling cannot mask a gap. Patients with fewer than two dated dispenses, or with no days-supply recorded, are EXCLUDED rather than scored zero — a blank field is a data-quality fact, not poor adherence. The headline is the share at 80% or better, the standard adherence threshold.',
    unit: '%',
    target: 80,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'adherence',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const per = mprByCase((rows && rows.cycles) || []);
      if (!per.length) return none('needs two dated dispenses with a days-supply recorded');
      const items = MPR_BANDS.map((b) => ({ l: b.l, v: 0, c: b.c }));
      for (const p of per) {
        for (let i = 0; i < MPR_BANDS.length; i++) {
          if (p.mpr >= MPR_BANDS[i].min) { items[i].v++; break; }
        }
      }
      const adherent = per.filter((p) => p.mpr >= 80).length;
      const v = pct(adherent, per.length);
      return {
        value: v,
        display: fmtPct(v),
        sub: fmtNum(adherent) + ' of ' + fmtNum(per.length) + ' scored patients at ≥80%',
        n: per.length,
        data: { items: items.filter((i) => i.v > 0), scored: per.length, median: median(per.map((p) => p.mpr)) },
      };
    },
  },
  {
    id: 'refill_ontime_trend',
    label: 'Refill on-time trend (proxy)',
    caveat:
      'PROXY. Share of consecutive cycle pairs no more than ' +
      REFILL_GRACE_DAYS +
      ' days apart, bucketed by the month of the LATER cycle. Same gap assumption as the headline punctuality metric — the schema has no per-cycle due date.',
    unit: '%',
    target: TARGETS.REFILL_ON_TIME_PCT,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'adherence',
    chart: 'line',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const per = cycleChains((rows && rows.cycles) || []);
      /** @type {Record<string, {t: number, o: number}>} */
      const byM = {};
      for (const id of Object.keys(per)) {
        const arr = per[id];
        for (let i = 1; i < arr.length; i++) {
          const g = daysBetween(arr[i - 1].created_at, arr[i].created_at);
          if (g === null) continue;
          const k = monthKey(arr[i].created_at);
          if (!k) continue;
          const cell = byM[k] || (byM[k] = { t: 0, o: 0 });
          cell.t++;
          if (g <= REFILL_GRACE_DAYS) cell.o++;
        }
      }
      const keys = Object.keys(byM).sort().slice(-12);
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: pct(byM[k].o, byM[k].t) || 0 }));
      const totals = keys.reduce((a, k) => ({ t: a.t + byM[k].t, o: a.o + byM[k].o }), { t: 0, o: 0 });
      const v = pct(totals.o, totals.t);
      if (v === null) return none('needs at least two cycles on one patient');
      return { value: v, display: fmtPct(v), sub: fmtNum(totals.t) + ' gaps', n: totals.t, data: { series } };
    },
  },
  {
    id: 'persistence_by_cycle',
    label: 'Persistence by cycle',
    caveat:
      'Patients reaching each refill cycle number, from medication_cycles.cycle_no. Read as a survival curve: the fall between two bars is the share of patients who did not come back for the next cycle. Cycles opened but never collected still count.',
    unit: 'patients',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'adherence',
    chart: 'bar',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<number, number>} */
      const counts = {};
      for (const c of (rows && rows.cycles) || []) {
        const n = Number((c && c.cycle_no) || 1);
        if (!isFinite(n) || n < 1) continue;
        counts[n] = (counts[n] || 0) + 1;
      }
      const keys = Object.keys(counts).map(Number).sort((a, b) => a - b).slice(0, 12);
      const series = keys.map((k) => ({ k: String(k), l: ordinal(k), v: counts[k] }));
      /** @type {{from: number, to: number, drop: number}|null} */
      let worst = null;
      for (let i = 1; i < keys.length; i++) {
        const prev = counts[keys[i - 1]];
        const cur = counts[keys[i]];
        if (prev > 0 && cur < prev) {
          const drop = pct(prev - cur, prev) || 0;
          if (!worst || drop > worst.drop) worst = { from: keys[i - 1], to: keys[i], drop };
        }
      }
      const total = series.reduce((a, s) => a + s.v, 0);
      return {
        value: total,
        display: fmtNum(total),
        sub: worst ? 'biggest drop: cycle ' + worst.from + ' → ' + worst.to + ' (−' + worst.drop + '%)' : 'no drop-off yet',
        data: { series, worst },
      };
    },
  },

  {
    id: 'treatment_start_confirmed',
    label: 'Treatment starts confirmed',
    caveat:
      'Share of patients with at least one dispense who also have a confirmed treatment start (medication_cycles.treatment_start_confirmed_at). The gap between the two is the silent failure mode of a patient support programme: medicine left the shelf, but nobody confirmed it reached the patient and was started. Denominator is patients with a dispense, not everyone enrolled.',
    unit: '%',
    target: 95,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'adherence',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const per = cycleChains((rows && rows.cycles) || []);
      const ids = Object.keys(per);
      if (!ids.length) return none('no dispenses in view');
      let confirmed = 0;
      for (const id of ids) if (per[id].some((y) => y && y.treatment_start_confirmed_at)) confirmed++;
      const v = pct(confirmed, ids.length);
      return {
        value: v,
        display: fmtPct(v),
        sub: fmtNum(confirmed) + ' of ' + fmtNum(ids.length) + ' dispensed patients confirmed',
        n: ids.length,
        data: {
          items: [
            { l: 'Start confirmed', v: confirmed, c: 'var(--ok-700)' },
            { l: 'Dispensed, unconfirmed', v: ids.length - confirmed, c: 'var(--warn-700)' },
          ].filter((i) => i.v > 0),
        },
      };
    },
  },
  {
    id: 'therapy_stops',
    label: 'Why therapy stopped',
    caveat:
      'Dispensing cycles closed with a reason recorded (medication_cycles.ended_reason), grouped by that reason. This is a CYCLE-level view, not a patient-level one: one patient can stop and restart, and each stop is counted. Cycles that simply ran their course without an ended_reason are not counted here. Free-text reasons are shown as captured, so spelling variants appear as separate bars until the vocabulary is coded.',
    unit: 'cycles',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'adherence',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const ranked = rankBy((rows && rows.cycles) || [], (y) => humanise(y && y.ended_reason), 8);
      const total = ranked.reduce((a, r) => a + r.v, 0);
      if (!total) return none('no cycle has a stop reason recorded');
      return {
        value: total,
        display: fmtNum(total),
        sub: 'cycles ended with a reason · top: ' + ranked[0].l,
        n: total,
        data: { items: ranked.map((r) => ({ l: r.l, v: r.v, c: 'var(--warn-700)' })) },
      };
    },
  },
  {
    id: 'medication_switches',
    label: 'Medication switches',
    caveat:
      'Dispensing cycles where the medicine changed and a reason was recorded (medication_cycles.medication_change_reason), grouped by reason. Switches matter for a support programme because each one is a moment the patient can fall out of the pathway — a new authorisation, a new price, a new counselling conversation. Counted per cycle, so a patient switched twice appears twice.',
    unit: 'switches',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.ANALYST,
    section: 'adherence',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const ranked = rankBy((rows && rows.cycles) || [], (y) => humanise(y && y.medication_change_reason), 8);
      const total = ranked.reduce((a, r) => a + r.v, 0);
      if (!total) return none('no medication changes recorded');
      const chains = cycleChains((rows && rows.cycles) || []);
      let patients = 0;
      for (const id of Object.keys(chains)) if (chains[id].some((y) => y && y.medication_change_reason)) patients++;
      return {
        value: total,
        display: fmtNum(total),
        sub: 'across ' + fmtNum(patients) + ' patients · top: ' + ranked[0].l,
        n: total,
        data: { items: ranked.map((r) => ({ l: r.l, v: r.v, c: 'var(--brand-700)' })), patients },
      };
    },
  },

  // ═══ Section: Retention & outcomes ══════════════════════════════════════
  {
    id: 'cohort_retention',
    label: 'Cohort retention (approximate)',
    caveat:
      'APPROXIMATE. Enrolment-quarter cohorts, showing the share still active at 3/6/9/12 months. There is no closed_at column, so a closed case is assumed to have closed at updated_at — any later edit to a closed row shifts its apparent exit date. Cohort points beyond today are not plotted (not yet observable). Exact once migration M2 lands lifecycle timestamps.',
    unit: '%',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'retention',
    chart: 'line',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const now = ctx && ctx.now ? ctx.now : new Date();
      /** @type {Record<string, any[]>} */
      const cohorts = {};
      for (const c of (rows && rows.cases) || []) {
        if (!c || !c.created_at) continue;
        const d = new Date(c.created_at);
        if (isNaN(d.getTime())) continue;
        const k = d.getFullYear() + ' Q' + (Math.floor(d.getMonth() / 3) + 1);
        (cohorts[k] || (cohorts[k] = [])).push(c);
      }
      const keys = Object.keys(cohorts).sort().slice(-6);
      const marks = [3, 6, 9, 12];
      const retainedAt = (c, m) => {
        const enrol = new Date(c.created_at);
        // Clamp the day to the target month's length. Left unclamped, the Date
        // constructor ROLLS an out-of-range day into the next month — a 31 Jan
        // enrolment gets a 3-month cut of 1 May instead of 30 Apr, so a case
        // closed on 30 April scores as retained. Same month-end trap monthPreset
        // in src/data/billing.js pins against.
        const cy = enrol.getFullYear();
        const cm = enrol.getMonth() + m;
        const dim = new Date(cy, cm + 1, 0).getDate();
        const cut = new Date(cy, cm, Math.min(enrol.getDate(), dim));
        if (cut > now) return null; // not yet observable
        if (isActiveCase(c)) return true;
        if (!c.updated_at) return false;
        const u = new Date(c.updated_at);
        return !isNaN(u.getTime()) && u >= cut;
      };
      const series = keys.map((k) => ({
        l: k,
        points: marks.map((m) => {
          let n = 0;
          let ret = 0;
          for (const c of cohorts[k]) {
            const r = retainedAt(c, m);
            if (r === null) continue;
            n++;
            if (r) ret++;
          }
          return { l: m + ' mo', v: pct(ret, n) };
        }),
      }));
      return { value: keys.length, display: fmtNum(keys.length), sub: keys.length === 1 ? 'cohort' : 'cohorts', data: { marks, series } };
    },
  },
  {
    id: 'closure_reasons',
    label: 'Why patients leave',
    caveat:
      'Closed cases grouped by archive_reason_code — the 16 structured reasons captured at closure. Cases closed before the reason field existed carry none and are absent from this chart, so the total here can be lower than the closed-case count.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'retention',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const counts = {};
      for (const c of (rows && rows.cases) || []) {
        const k = c && c.archive_reason_code;
        if (k) counts[k] = (counts[k] || 0) + 1;
      }
      const items = Object.keys(counts)
        .map((k) => {
          const r = ARCHIVE_REASONS.find((x) => x.value === k);
          const sev = r && r.severity;
          return {
            l: r ? r.label : String(k).replace(/_/g, ' '),
            v: counts[k],
            c: sev === 'danger' ? 'var(--danger-700)' : sev === 'warn' ? 'var(--warn-700)' : sev === 'ok' ? 'var(--ok-700)' : 'var(--brand-700)',
          };
        })
        .sort((a, b) => b.v - a.v);
      const total = items.reduce((a, i) => a + i.v, 0);
      return { value: total, display: fmtNum(total), sub: total === 1 ? '1 closure with a reason' : fmtNum(total) + ' closures with a reason', data: { items } };
    },
  },
  {
    id: 'at_risk_patients',
    label: 'At-risk patients',
    caveat:
      'Active cases flagged high or critical on risk_bucket. STAFF ONLY: this is a named patient list for follow-up, and it is excluded from every export (Kenya DPA 2019 — exports carry aggregates, never patient-level rows).',
    unit: 'patients',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'retention',
    isNew: true,
    exportable: false, // never leaves the screen
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const list = ((rows && rows.cases) || [])
        .filter((c) => isActiveCase(c) && (c.risk_bucket === 'high' || c.risk_bucket === 'critical'))
        .sort((a, b) => (a.risk_bucket === 'critical' ? 0 : 1) - (b.risk_bucket === 'critical' ? 0 : 1));
      return { value: list.length, display: fmtNum(list.length), sub: list.length === 1 ? '1 flagged' : fmtNum(list.length) + ' flagged', data: { list } };
    },
  },
  {
    id: 'retention_by_month',
    label: 'Retention by enrolment month',
    caveat:
      'For each enrolment month: patients enrolled, how many are still active, and the share. A simple point-in-time snapshot — unlike the cohort curves it does not follow a cohort over time.',
    unit: '%',
    goodDirection: GOOD.UP,
    tier: TIER.ANALYST,
    section: 'retention',
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, {init: number, act: number}>} */
      const by = {};
      for (const c of (rows && rows.cases) || []) {
        const k = monthKey(c && c.created_at);
        if (!k) continue;
        const cell = by[k] || (by[k] = { init: 0, act: 0 });
        cell.init++;
        if (isActiveCase(c)) cell.act++;
      }
      const keys = Object.keys(by).sort();
      const list = keys.map((k) => ({ l: monthLabel(k), init: by[k].init, act: by[k].act, pct: pct(by[k].act, by[k].init) }));
      const totals = keys.reduce((a, k) => ({ init: a.init + by[k].init, act: a.act + by[k].act }), { init: 0, act: 0 });
      const v = pct(totals.act, totals.init);
      return { value: v, display: fmtPct(v), sub: 'still active overall', n: totals.init, data: { rows: list } };
    },
  },

  {
    id: 'delivery_method_mix',
    label: 'How medicine reaches patients',
    caveat:
      'Active patients grouped by cases.delivery_method — courier, pickup, or however the programme routes medicine to them. Read beside retention: a delivery route that correlates with drop-off is one of the few retention levers a support programme can actually pull without clinical change. Patients with no method recorded default to the collection point on the ground, so a large blank share usually means the field is not being filled rather than that nobody collects.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'retention',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const act = ((rows && rows.cases) || []).filter(isActiveCase);
      if (!act.length) return none('no active patients in view');
      const ranked = rankBy(act, (c) => humanise(c && c.delivery_method), 6);
      const known = ranked.reduce((a, r) => a + r.v, 0);
      if (!known) return none('no delivery method recorded on any active patient');
      const missing = act.length - known;
      return {
        value: known,
        display: fmtNum(known),
        sub: 'routed · top: ' + ranked[0].l + (missing > 0 ? ' · ' + fmtNum(missing) + ' unrecorded' : ''),
        n: act.length,
        data: { items: ranked.map((r) => ({ l: r.l, v: r.v, c: 'var(--brand-700)' })), missing },
      };
    },
  },

  // ═══ Section: Safety ════════════════════════════════════════════════════
  {
    id: 'ae_trend',
    label: 'Adverse-event trend',
    caveat:
      'Adverse events per calendar month, split into all events and the serious subset. Bucketed on adverse_events.created_at — the moment the event was REPORTED into the app, which may be later than the onset date.',
    unit: 'events/month',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'safety',
    chart: 'bar',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, {all: number, serious: number}>} */
      const by = {};
      for (const a of (rows && rows.aes) || []) {
        const k = monthKey(a && a.created_at);
        if (!k) continue;
        const cell = by[k] || (by[k] = { all: 0, serious: 0 });
        cell.all++;
        if (a.seriousness === 'serious') cell.serious++;
      }
      const keys = Object.keys(by).sort().slice(-12);
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: by[k].all, serious: by[k].serious }));
      const total = series.reduce((a, s) => a + s.v, 0);
      const serious = series.reduce((a, s) => a + s.serious, 0);
      return { value: total, display: fmtNum(total), sub: fmtNum(serious) + ' serious', data: { series } };
    },
  },
  {
    id: 'ae_rate_per_100',
    label: 'AE rate per 100 active',
    caveat:
      'Adverse events in a month ÷ active patients in view × 100. Normalising by caseload separates a real safety signal from a growth artefact. The denominator is TODAY\'s active count applied to every month — the schema has no historical active-count snapshot, so earlier months are understated when the programme has grown.',
    unit: 'per 100',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'safety',
    chart: 'line',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const active = ((rows && rows.cases) || []).filter(isActiveCase).length;
      /** @type {Record<string, number>} */
      const by = {};
      for (const a of (rows && rows.aes) || []) {
        const k = monthKey(a && a.created_at);
        if (k) by[k] = (by[k] || 0) + 1;
      }
      const keys = Object.keys(by).sort().slice(-12);
      const series = keys.map((k) => {
        const r = safeDiv(by[k] * 100, active);
        return { k, l: monthLabel(k), v: r === null ? 0 : Math.round(r * 10) / 10 };
      });
      const latest = series.length ? series[series.length - 1].v : null;
      if (latest === null || !active) return none(active ? 'no adverse events in view' : 'no active patients in view');
      return { value: latest, display: String(latest), sub: 'latest month · per 100 of ' + fmtNum(active) + ' active', n: active, data: { series } };
    },
  },
  {
    id: 'ae_reporting_timeliness',
    label: 'PV reporting within 24 h',
    caveat:
      'Share of adverse events dispatched to pharmacovigilance within 24 hours, measured from adverse_events.created_at (recorded in the app) to reported_to_pv_at (dispatch stamped by the app). Denominator is events that HAVE been dispatched — events still waiting are excluded here and counted on the PV dispatch backlog tile instead, so a growing backlog can never flatter this percentage. The clock starts when the event was RECORDED, not when it began: recording delay is measured separately on the onset-to-record tile.',
    unit: '%',
    target: 100,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'safety',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const aes = (rows && rows.aes) || [];
      let sent = 0;
      let within = 0;
      let pending = 0;
      for (const a of aes) {
        if (!a || !a.created_at) continue;
        if (!a.reported_to_pv_at) { pending++; continue; }
        const d = daysBetween(a.created_at, a.reported_to_pv_at);
        if (d === null || d < 0) continue;
        sent++;
        if (d <= 1) within++;
      }
      const v = pct(within, sent);
      if (v === null) return none(pending ? fmtNum(pending) + ' recorded, none dispatched yet' : 'no adverse events in view');
      return {
        value: v,
        display: fmtPct(v),
        sub: fmtNum(within) + ' of ' + fmtNum(sent) + ' dispatched in 24 h' + (pending ? ' · ' + fmtNum(pending) + ' still pending' : ''),
        n: sent,
        data: { within, sent, pending },
      };
    },
  },

  {
    id: 'pv_dispatch_backlog',
    label: 'PV dispatch backlog',
    caveat:
      'Adverse events recorded in the app but with no reported_to_pv_at stamp — i.e. not yet sent to pharmacovigilance. This is a POINT-IN-TIME queue, not a period count: it answers "what is waiting right now". Ageing is measured from adverse_events.created_at to now. The serious-over-48-hours figure is the regulatory red line and is shown even when it is zero, because a blank there must mean "checked and clear", not "not looked at".',
    unit: 'events',
    target: 0,
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'safety',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const now = (ctx && ctx.now instanceof Date && !isNaN(ctx.now.getTime()) ? ctx.now : new Date()).getTime();
      const open = ((rows && rows.aes) || []).filter((a) => a && a.created_at && !a.reported_to_pv_at);
      if (!open.length) return { value: 0, display: '0', sub: 'nothing waiting to be dispatched', n: 0, data: { items: [], serious48: 0 } };
      let serious48 = 0;
      let oldest = 0;
      const ages = [];
      for (const a of open) {
        const age = (now - new Date(a.created_at).getTime()) / 86400000;
        if (!isFinite(age) || age < 0) continue;
        ages.push(age);
        if (age > oldest) oldest = age;
        if (a.seriousness === 'serious' && age > 2) serious48++;
      }
      const seriousN = open.filter((a) => a.seriousness === 'serious').length;
      return {
        value: open.length,
        display: fmtNum(open.length),
        sub: fmtNum(seriousN) + ' serious · oldest ' + fmtDays(oldest),
        n: open.length,
        data: {
          items: [
            { l: 'Serious, over 48 h', v: serious48, c: 'var(--danger-700)' },
            { l: 'Serious, within 48 h', v: Math.max(0, seriousN - serious48), c: 'var(--warn-700)' },
            { l: 'Non-serious', v: Math.max(0, open.length - seriousN), c: 'var(--brand-700)' },
          ].filter((i) => i.v > 0),
          serious48,
          medianAge: median(ages),
        },
      };
    },
  },
  {
    id: 'ae_severity_mix',
    label: 'AE severity mix',
    caveat:
      'Adverse events in view split by adverse_events.severity, using the SAME grade list the AE form offers (mild → moderate → severe → life-threatening → fatal) and held in that clinical order rather than re-ranked by volume, so the shape is comparable month to month. The headline counts severe, life-threatening and fatal together — "severe or worse". Events with no severity recorded are reported as a completeness figure beneath the bars rather than being dropped, because a missing grade on a serious event is itself the finding.',
    unit: 'events',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'safety',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const aes = (rows && rows.aes) || [];
      const mix = orderedMix(aes, 'severity', AE_SEVERITY_VOCAB);
      if (!mix.total) return none('no adverse events in view');
      // Counted off the ROWS, not off the bar labels: a headline that reads its
      // own chart captions breaks the moment a label is reworded.
      const severe = aes.filter((a) => AE_SEVERE_OR_WORSE.includes(String((a && a.severity) || '').trim().toLowerCase())).length;
      const graded = mix.total - mix.missing;
      return {
        value: severe,
        display: fmtNum(severe),
        sub: 'severe or worse, of ' + fmtNum(graded) + ' graded' + (mix.missing ? ' · ' + fmtNum(mix.missing) + ' ungraded' : ''),
        n: graded,
        data: { items: mix.items, missing: mix.missing, total: mix.total },
      };
    },
  },
  {
    id: 'ae_outcome_mix',
    label: 'AE outcomes',
    caveat:
      'How adverse events in view resolved, from adverse_events.outcome, in fixed clinical order. "Not recovered" counts events still unresolved at the last update — it is not a mortality measure. Events with no outcome recorded are reported separately beneath the bars; an ageing pile of blank outcomes usually means follow-up stopped, not that patients are fine.',
    unit: 'events',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'safety',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const aes = (rows && rows.aes) || [];
      const mix = orderedMix(aes, 'outcome', AE_OUTCOME_VOCAB);
      if (!mix.total) return none('no adverse events in view');
      // Strictly 'recovered' — recovery WITH lasting effects is a different
      // outcome and is shown on its own bar rather than folded in to flatter this.
      const resolved = aes.filter((a) => String((a && a.outcome) || '').trim().toLowerCase() === 'recovered').length;
      const known = mix.total - mix.missing;
      const v = pct(resolved, known);
      return {
        value: v,
        display: fmtPct(v),
        sub: 'recovered, of ' + fmtNum(known) + ' with an outcome' + (mix.missing ? ' · ' + fmtNum(mix.missing) + ' blank' : ''),
        n: known,
        data: { items: mix.items, missing: mix.missing },
      };
    },
  },
  {
    id: 'ae_onset_lag',
    label: 'Onset-to-record lag',
    caveat:
      'Median days from adverse_events.onset_date (when the patient says it began) to created_at (when it reached the app). This is the capture delay, and it is the clock that decides whether the 24-hour pharmacovigilance window was ever winnable — an event recorded five days late cannot be dispatched on time no matter how fast the desk works. Events with no onset date are excluded from the median and counted as a completeness figure.',
    unit: 'days',
    target: 1,
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'safety',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const aes = (rows && rows.aes) || [];
      const lags = [];
      let missing = 0;
      let total = 0;
      for (const a of aes) {
        if (!a || !a.created_at) continue;
        total++;
        if (!a.onset_date) { missing++; continue; }
        const d = daysBetween(a.onset_date, a.created_at);
        if (d === null || d < 0 || d > 365) { missing++; continue; }
        lags.push(d);
      }
      if (!total) return none('no adverse events in view');
      const m = median(lags);
      if (m === null) return none(fmtNum(missing) + ' events, none with a usable onset date');
      const sameDay = lags.filter((d) => d < 1).length;
      return {
        value: Math.round(m * 10) / 10,
        display: fmtDays(m),
        sub: fmtNum(sameDay) + ' of ' + fmtNum(lags.length) + ' same-day' + (missing ? ' · ' + fmtNum(missing) + ' without onset' : ''),
        n: lags.length,
        data: { median: m, sameDay, scored: lags.length, missing },
      };
    },
  },
  {
    id: 'toxicity_by_cycle',
    label: 'Toxicity timing by cycle',
    caveat:
      'Adverse events grouped by the treatment cycle number they were reported against (adverse_events.cycle_no), beside the number of patients who actually reached that cycle. Read the two together: 10 events at cycle 1 across 200 patients is a different signal from 10 at cycle 6 across 12. Events with no cycle number recorded are excluded. This is a timing pattern, not a causality test.',
    unit: 'events',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.ANALYST,
    section: 'safety',
    chart: 'bar',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const byCycle = {};
      let scored = 0;
      for (const a of (rows && rows.aes) || []) {
        const n = Number(a && a.cycle_no);
        if (!isFinite(n) || n < 1 || n > 60) continue;
        byCycle[n] = (byCycle[n] || 0) + 1;
        scored++;
      }
      if (!scored) return none('no adverse events carry a cycle number');
      /** @type {Record<string, Set<string>>} */
      const reached = {};
      for (const y of (rows && rows.cycles) || []) {
        const n = Number(y && y.cycle_no);
        if (!y || !y.case_id || !isFinite(n) || n < 1 || n > 60) continue;
        (reached[n] || (reached[n] = new Set())).add(y.case_id);
      }
      const keys = Object.keys(byCycle)
        .map(Number)
        .sort((a, b) => a - b)
        .slice(0, 12);
      const series = keys.map((k) => ({
        k: String(k),
        l: 'Cycle ' + k,
        v: byCycle[k],
        patients: (reached[k] && reached[k].size) || 0,
      }));
      const peak = series.reduce((a, s) => (s.v > a.v ? s : a), series[0]);
      return {
        value: scored,
        display: fmtNum(scored),
        sub: 'events with a cycle · peak at ' + peak.l,
        n: scored,
        data: { series, peak: peak.l },
      };
    },
  },

  // ═══ Section: Reachability ══════════════════════════════════════════════
  {
    id: 'comms_channel_mix',
    label: 'Comms permission mix',
    caveat:
      'Cases by the channels the patient consented to (comms_consent). A patient who consented to several channels is counted once per channel, so the parts sum to more than the patient count; "none / unknown" are patients with no channel consented or no record at all.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'reach',
    chart: 'doughnut',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const ch = { call: 0, sms: 0, whatsapp: 0, email: 0 };
      let none_ = 0;
      for (const c of (rows && rows.cases) || []) {
        const cc = (c && c.comms_consent) || {};
        let any = false;
        for (const k of Object.keys(ch)) {
          if (cc[k] === true) {
            ch[/** @type {'call'} */ (k)]++;
            any = true;
          }
        }
        if (!any) none_++;
      }
      const total = ((rows && rows.cases) || []).length;
      return {
        value: total - none_,
        display: fmtNum(total - none_),
        sub: 'with at least one channel',
        n: total,
        data: {
          items: [
            { l: 'Call', v: ch.call, c: '#1F4477' },
            { l: 'SMS', v: ch.sms, c: '#0891B2' },
            { l: 'WhatsApp', v: ch.whatsapp, c: '#65A30D' },
            { l: 'Email', v: ch.email, c: '#7C3AED' },
            { l: 'None / unknown', v: none_, c: '#94A3B8' },
          ],
        },
      };
    },
  },
  {
    id: 'reach_rate_by_channel',
    label: 'Reach rate by channel',
    caveat:
      'Of the contact attempts logged in the app, the share that actually reached the patient or their caregiver, split by the channel used. Numerator is contacts.outcome starting "reached"; denominator is every logged attempt on that channel. It measures ATTEMPTS THAT WERE LOGGED — an attempt nobody wrote down cannot appear, so a channel the team uses informally will look unused rather than unsuccessful.',
    unit: '%',
    target: 70,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'reach',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const ctc = (rows && rows.contacts) || [];
      if (!ctc.length) return none('no contact attempts logged in view');
      /** @type {Record<string, {t: number, r: number}>} */
      const by = {};
      let total = 0;
      let reached = 0;
      for (const x of ctc) {
        const ch = humanise((x && x.channel) || '') || 'Not recorded';
        const hit = String((x && x.outcome) || '').trim().toLowerCase().startsWith('reached');
        const cell = by[ch] || (by[ch] = { t: 0, r: 0 });
        cell.t++;
        total++;
        if (hit) { cell.r++; reached++; }
      }
      const items = Object.keys(by)
        .map((l) => {
          const v = pct(by[l].r, by[l].t) || 0;
          return { l, v, sub: v + '% of ' + fmtNum(by[l].t), c: v >= 70 ? 'var(--ok-700)' : v >= 40 ? 'var(--warn-700)' : 'var(--danger-700)' };
        })
        .sort((a, b) => b.v - a.v);
      const overall = pct(reached, total);
      return {
        value: overall,
        display: fmtPct(overall),
        sub: fmtNum(reached) + ' of ' + fmtNum(total) + ' logged attempts connected',
        n: total,
        data: { items, total, reached },
      };
    },
  },
  {
    id: 'preference_honoured',
    label: 'Preferred channel honoured',
    caveat:
      'Contact attempts made on a channel the patient actually consented to, as a share of all attempts on patients whose comms_consent is recorded. This is a DATA-PROTECTION measure, not a service one: every point below 100% is an attempt made on a channel the patient did not agree to. Attempts on patients with no recorded preference are excluded from the denominator and counted separately — an unknown preference cannot be honoured or breached, but it should not be silently scored as either.',
    unit: '%',
    target: 100,
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'reach',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const ctc = (rows && rows.contacts) || [];
      if (!ctc.length) return none('no contact attempts logged in view');
      /** @type {Record<string, any>} */
      const consentByCase = {};
      for (const c of (rows && rows.cases) || []) if (c && c.case_id) consentByCase[c.case_id] = c.comms_consent;
      let scored = 0;
      let honoured = 0;
      let unknownPref = 0;
      const breaches = [];
      for (const x of ctc) {
        const cc = x && consentByCase[x.case_id];
        const hasPref = cc && typeof cc === 'object' && ['call', 'sms', 'whatsapp', 'email'].some((k) => typeof cc[k] === 'boolean');
        if (!hasPref) { unknownPref++; continue; }
        const ch = String((x && x.channel) || '').trim().toLowerCase();
        // 'call' is the app's default when a channel was not chosen explicitly.
        const key = ch === 'phone' ? 'call' : ch;
        scored++;
        if (cc[key] === true) honoured++;
        else breaches.push(key || '(unrecorded)');
      }
      if (!scored) return none(fmtNum(unknownPref) + ' attempts, none on a patient with a recorded preference');
      const v = pct(honoured, scored);
      const offItems = rankBy(breaches.map((b) => ({ b })), (r) => humanise(r.b), 5);
      return {
        value: v,
        display: fmtPct(v),
        sub:
          fmtNum(honoured) + ' of ' + fmtNum(scored) + ' attempts on a consented channel' +
          (unknownPref ? ' · ' + fmtNum(unknownPref) + ' with no preference on file' : ''),
        n: scored,
        data: {
          items: [
            { l: 'On a consented channel', v: honoured, c: 'var(--ok-700)' },
            { l: 'Not consented', v: scored - honoured, c: 'var(--danger-700)' },
          ].filter((i) => i.v > 0),
          offChannel: offItems.map((r) => ({ l: r.l, v: r.v, c: 'var(--danger-700)' })),
          unknownPref,
        },
      };
    },
  },
  {
    id: 'no_contact_permission',
    label: 'No contact permission',
    caveat:
      'Active cases with no channel consented at all — the programme cannot lawfully call, text or email them, so every follow-up must go through the clinic. Denominator: active cases in view.',
    unit: 'patients',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'reach',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const act = ((rows && rows.cases) || []).filter(isActiveCase);
      const n = act.filter((c) => !hasCommsConsent(c)).length;
      const share = pct(n, act.length);
      return { value: n, display: fmtNum(n), sub: share === null ? '' : share + '% of active', n: act.length };
    },
  },

  // ═══ Section: Financing & cost ══════════════════════════════════════════
  {
    id: 'cost_breakdown',
    label: 'Pass-through cost breakdown',
    caveat:
      'Sum of expenses.amount by category, excluding soft-deleted rows. Pass-through programme spend only — no staff time, no overheads. Expenses are dated on expense_date; on the expense clock the period filters on that date.',
    unit: 'KSh',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'finance',
    chart: 'doughnut',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const e of (rows && rows.expenses) || []) {
        const k = String((e && e.category) || 'other');
        const n = Number((e && e.amount) || 0);
        by[k] = (by[k] || 0) + (isFinite(n) ? n : 0);
      }
      const palette = ['#1F4477', '#0891B2', '#65A30D', '#D97706', '#7C3AED', '#94A3B8', '#B91C1C', '#2B5BA8'];
      const items = Object.keys(by)
        .map((k) => ({ l: String(k).replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase()), v: Math.round(by[k]), c: '' }))
        .sort((a, b) => b.v - a.v)
        .map((it, i) => Object.assign(it, { c: palette[i % palette.length] }));
      const total = items.reduce((a, i) => a + i.v, 0);
      return { value: total, display: fmtMoney(total), sub: items.length + (items.length === 1 ? ' category' : ' categories'), data: { items, total } };
    },
  },
  {
    id: 'cost_per_patient_trend',
    label: 'Cost per active patient by month',
    caveat:
      'Monthly pass-through spend ÷ active patients in view. The denominator is TODAY\'s active count applied to every month (no historical caseload snapshot exists), so earlier months read low in a growing programme. Direction is meaningful; the absolute level is a floor.',
    unit: 'KSh',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'finance',
    chart: 'line',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const active = ((rows && rows.cases) || []).filter(isActiveCase).length;
      /** @type {Record<string, number>} */
      const by = {};
      for (const e of (rows && rows.expenses) || []) {
        const k = monthKey((e && e.expense_date) || (e && e.created_at));
        if (!k) continue;
        const n = Number((e && e.amount) || 0);
        by[k] = (by[k] || 0) + (isFinite(n) ? n : 0);
      }
      const keys = Object.keys(by).sort().slice(-12);
      const series = keys.map((k) => {
        const r = safeDiv(by[k], active);
        return { k, l: monthLabel(k), v: r === null ? 0 : Math.round(r) };
      });
      const latest = series.length ? series[series.length - 1].v : null;
      if (latest === null || !active) return none(active ? 'no expenses in view' : 'no active patients in view');
      return { value: latest, display: fmtMoney(latest), sub: 'latest month', n: active, data: { series } };
    },
  },
  {
    id: 'cost_by_programme',
    label: 'Cost per patient by programme',
    caveat:
      'Pass-through spend attributed to each programme ÷ that programme\'s active cases in view. Expenses with no programme_id are grouped as unattributed rather than spread across programmes.',
    unit: 'KSh',
    goodDirection: GOOD.DOWN,
    tier: TIER.ANALYST,
    section: 'finance',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const progs = (ctx && ctx.programmes) || [];
      /** @type {Record<string, number>} */
      const spend = {};
      for (const e of (rows && rows.expenses) || []) {
        const k = String((e && e.programme_id) || '_none');
        const n = Number((e && e.amount) || 0);
        spend[k] = (spend[k] || 0) + (isFinite(n) ? n : 0);
      }
      /** @type {Record<string, number>} */
      const heads = {};
      for (const c of (rows && rows.cases) || []) {
        if (!isActiveCase(c)) continue;
        const k = String((c && c.programme_id) || '_none');
        heads[k] = (heads[k] || 0) + 1;
      }
      const items = Object.keys(spend)
        .map((k) => {
          const p = progs.find((x) => x && x.programme_id === k);
          const per = safeDiv(spend[k], heads[k] || 0);
          return {
            l: p ? p.display_name || p.code || k : k === '_none' ? 'Unattributed' : k,
            v: per === null ? 0 : Math.round(per),
            sub: per === null ? 'no active patients' : fmtMoney(per),
            c: 'var(--brand-700)',
          };
        })
        .sort((a, b) => b.v - a.v);
      const total = sumAmount((rows && rows.expenses) || []);
      return { value: total, display: fmtMoney(total), sub: 'total attributed spend', data: { items } };
    },
  },

  {
    id: 'billable_split',
    label: 'Sponsor-billable split',
    caveat:
      'Spend in view split by expenses.is_billable — what can be recovered from the sponsor versus what the programme absorbs. This is the number that decides whether the programme is financially sustainable at its current size, and it is the one a finance reviewer reconciles first. Soft-deleted expenses are already excluded at source, so this total always agrees with the Billing screen.',
    unit: 'KSh',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'finance',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const exp = (rows && rows.expenses) || [];
      if (!exp.length) return none('no expenses in view');
      const billable = sumAmount(exp.filter((e) => e && e.is_billable === true));
      const absorbed = sumAmount(exp.filter((e) => e && e.is_billable !== true));
      const total = billable + absorbed;
      if (!total) return none('expenses in view carry no amount');
      const v = pct(billable, total);
      return {
        value: v,
        display: fmtPct(v),
        sub: 'billable · ' + fmtMoney(billable) + ' of ' + fmtMoney(total),
        n: exp.length,
        data: {
          items: [
            { l: 'Sponsor-billable', v: billable, c: 'var(--ok-700)' },
            { l: 'Programme absorbs', v: absorbed, c: 'var(--warn-700)' },
          ].filter((i) => i.v > 0),
          billable,
          absorbed,
        },
      };
    },
  },
  {
    id: 'spend_run_rate',
    label: 'Spend run-rate',
    caveat:
      'Average monthly spend over the last three COMPLETE calendar months, with the implied next-12-month figure. The current month is deliberately excluded — a month that is one week old always reads as a collapse in spending and has panicked more than one board. A run-rate is a straight-line projection of recent behaviour, not a budget: it assumes the caseload and the mix stay as they are.',
    unit: 'KSh',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'finance',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const now = ctx && ctx.now instanceof Date && !isNaN(ctx.now.getTime()) ? ctx.now : new Date();
      /** @type {Record<string, number>} */
      const byM = {};
      for (const e of (rows && rows.expenses) || []) {
        const k = monthKey(e && (e.expense_date || e.created_at));
        if (!k) continue;
        const n = Number((e && e.amount) || 0);
        if (isFinite(n)) byM[k] = (byM[k] || 0) + n;
      }
      // The last three months that have actually finished (drop the current one).
      const complete = lastMonthKeys(4, now).slice(0, 3);
      const vals = complete.map((k) => byM[k] || 0);
      const spent = vals.reduce((a, v) => a + v, 0);
      if (!spent) return none('no spend in the last three complete months');
      const rate = safeDiv(spent, 3);
      if (rate === null) return none('no spend in the last three complete months');
      return {
        value: Math.round(rate),
        display: fmtMoney(rate),
        sub: 'per month · ' + fmtMoney(rate * 12) + ' a year at this rate',
        n: 3,
        data: {
          items: complete.map((k) => ({ l: monthLabel(k), v: byM[k] || 0, c: 'var(--brand-700)', sub: fmtMoney(byM[k] || 0) })),
          annualised: rate * 12,
        },
      };
    },
  },

  // ═══ Section: Geography & equity ════════════════════════════════════════
  {
    id: 'patients_by_county',
    label: 'Patients by county',
    caveat:
      'Cases per county, top 15. Counties are free text on the case row, so spelling variants count separately — the data-quality card tracks how many cases have no county at all. Per-100k-population would need a county population lookup we do not ship yet.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'geo',
    chart: 'bar',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const items = rankBy((rows && rows.cases) || [], (c) => c.county, 15);
      const total = items.reduce((a, i) => a + i.v, 0);
      return { value: items.length, display: fmtNum(items.length), sub: fmtNum(total) + ' located patients', data: { items } };
    },
  },
  {
    id: 'programme_county_heat',
    label: 'Programme × county reach',
    caveat:
      'Active cases per programme in each of the top counties. Empty cells are genuine access gaps — a programme with no patient in a county — not missing data.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.ANALYST,
    section: 'geo',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const progs = (ctx && ctx.programmes) || [];
      const cases = (rows && rows.cases) || [];
      const tops = rankBy(cases, (c) => c.county, 8).map((i) => i.l);
      /** @type {Record<string, number>} */
      const cell = {};
      for (const c of cases) {
        const cty = String((c && c.county) || '').trim();
        if (!cty) continue;
        cell[String(c.programme_id) + '|' + cty] = (cell[String(c.programme_id) + '|' + cty] || 0) + 1;
      }
      const grid = progs.map((p) => ({
        l: p.code || p.display_name || p.programme_id,
        cells: tops.map((t) => cell[String(p.programme_id) + '|' + t] || 0),
      }));
      let max = 0;
      for (const r of grid) for (const v of r.cells) if (v > max) max = v;
      return { value: tops.length, display: fmtNum(tops.length), sub: 'counties × ' + fmtNum(progs.length) + ' programmes', data: { cols: tops, grid, max } };
    },
  },
  {
    id: 'urban_rural',
    label: 'Urban vs rural reach (approximate)',
    caveat:
      'APPROXIMATE. Counties are classified urban if they are one of ' +
      URBAN_COUNTIES.length +
      ' predominantly-urban counties (Nairobi, Mombasa, Kisumu, Nakuru, Kiambu); every other county counts as rural. This is a county-level proxy — a rural patient in a mostly-urban county reads as urban. Cases without a county are counted separately, never assigned a side.',
    unit: '%',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'geo',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      let urban = 0;
      let rural = 0;
      let unknown = 0;
      for (const c of (rows && rows.cases) || []) {
        const k = String((c && c.county) || '').trim().toLowerCase();
        if (!k) unknown++;
        else if (URBAN_COUNTIES.includes(k)) urban++;
        else rural++;
      }
      const known = urban + rural;
      const ruralPct = pct(rural, known);
      return {
        value: ruralPct,
        display: fmtPct(ruralPct),
        sub: ruralPct === null ? 'no county data yet' : 'of located patients are rural',
        n: known,
        data: {
          items: [
            { l: 'Urban', v: urban, c: '#1F4477' },
            { l: 'Rural', v: rural, c: '#65A30D' },
          ],
          unknown,
        },
      };
    },
  },
  {
    id: 'patients_by_facility',
    label: 'Patients by facility',
    caveat: 'Cases per treating facility, top 15. Facility names are free text on the case row, so spelling variants count separately.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.ANALYST,
    section: 'geo',
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const items = rankBy((rows && rows.cases) || [], (c) => c.treating_facility || c.facility_name, 15);
      const total = items.reduce((a, i) => a + i.v, 0);
      return { value: items.length, display: fmtNum(items.length), sub: fmtNum(total) + ' placed patients', data: { items } };
    },
  },
  {
    id: 'top_prescribers',
    label: 'Top prescribers',
    caveat: 'Cases per treating physician, top 15, from the free-text physician_name on the case. Two spellings of one doctor count as two prescribers.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.ANALYST,
    section: 'geo',
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const items = rankBy((rows && rows.cases) || [], (c) => c.physician_name, 15);
      return { value: items.length, display: fmtNum(items.length), sub: items.length === 1 ? 'prescriber' : 'prescribers', data: { items } };
    },
  },

  {
    id: 'age_mix',
    label: 'Age distribution',
    caveat:
      'Patients in view grouped into age bands from cases.dob, computed at the report date. Bands are held in age order, never volume order. Patients with no date of birth fall into the "—" band, which is shown rather than hidden: an unknown age blocks eligibility checks and dosing review, so it is an operational gap, not a cosmetic one.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'geo',
    chart: 'bar',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const cases = (rows && rows.cases) || [];
      if (!cases.length) return none('no cases in view');
      const now = ctx && ctx.now instanceof Date && !isNaN(ctx.now.getTime()) ? ctx.now : new Date();
      const ORDER = ['Under 18', '18–29', '30–44', '45–59', '60–74', '75+', '—'];
      /** @type {Record<string, number>} */
      const counts = {};
      for (const c of cases) {
        const b = ageBand(c && c.dob, now);
        counts[b] = (counts[b] || 0) + 1;
      }
      const series = ORDER.filter((b) => counts[b]).map((b) => ({ k: b, l: b === '—' ? 'Not recorded' : b, v: counts[b] }));
      const known = cases.length - (counts['—'] || 0);
      if (!known) return none('no date of birth recorded on any case in view');
      const top = series.filter((s) => s.k !== '—').reduce((a, s) => (s.v > a.v ? s : a), { k: '', l: '', v: 0 });
      return {
        value: known,
        display: fmtNum(known),
        sub: 'with an age · most in ' + top.l + (counts['—'] ? ' · ' + fmtNum(counts['—']) + ' unrecorded' : ''),
        n: cases.length,
        data: { series, missing: counts['—'] || 0 },
      };
    },
  },
  {
    id: 'sex_mix',
    label: 'Sex balance',
    caveat:
      'Patients in view grouped by cases.sex as recorded at intake. Read against the expected epidemiology of the indication: a breast-cancer programme skewing male, or a prostate programme with a large female share, is a data-entry problem, and any large imbalance against the disease norm is an access-equity question worth asking. Cases with no sex recorded are counted separately.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'geo',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      if (!cases.length) return none('no cases in view');
      const ranked = rankBy(cases, (c) => humanise(c && c.sex), 6);
      const known = ranked.reduce((a, r) => a + r.v, 0);
      if (!known) return none('no sex recorded on any case in view');
      const missing = cases.length - known;
      const tone = { Female: 'var(--brand-700)', Male: 'var(--ok-700)' };
      return {
        value: pct(ranked[0].v, known),
        display: fmtPct(pct(ranked[0].v, known)),
        sub: ranked[0].l.toLowerCase() + ' of ' + fmtNum(known) + ' recorded' + (missing > 0 ? ' · ' + fmtNum(missing) + ' blank' : ''),
        n: known,
        data: { items: ranked.map((r) => ({ l: r.l, v: r.v, c: tone[r.l] || 'var(--ink-muted)' })), missing },
      };
    },
  },

  // ═══ Section: Team & workload ═══════════════════════════════════════════
  {
    id: 'nurse_caseload',
    label: 'Nurse caseload',
    caveat:
      'Active cases per owning staff member, from the case owner join; cases with no owner are grouped as Unassigned. Counts live caseload, not work done — a nurse who closed 40 cases this month shows only what she still holds.',
    unit: 'patients',
    goodDirection: GOOD.NEUTRAL,
    tier: TIER.BOARD,
    section: 'team',
    isEnhanced: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const c of (rows && rows.cases) || []) {
        if (!isActiveCase(c)) continue;
        const n = (c.owner && c.owner.full_name) || 'Unassigned';
        by[n] = (by[n] || 0) + 1;
      }
      const items = Object.keys(by)
        .map((l) => ({ l, v: by[l], c: l === 'Unassigned' ? 'var(--danger-700)' : 'var(--brand-700)' }))
        .sort((a, b) => b.v - a.v);
      const unassigned = by['Unassigned'] || 0;
      const total = items.reduce((a, i) => a + i.v, 0);
      return { value: total, display: fmtNum(total), sub: unassigned ? fmtNum(unassigned) + ' unassigned' : 'all assigned', data: { items, unassigned } };
    },
  },
  {
    id: 'caseload_balance',
    label: 'Caseload balance',
    caveat:
      'Largest staff caseload ÷ smallest, over ASSIGNED cases only (Unassigned is excluded — it is a backlog, not a person). 1.0 means perfectly even. Needs at least two staff members with cases; below that there is nothing to balance.',
    unit: '×',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'team',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const c of (rows && rows.cases) || []) {
        if (!isActiveCase(c)) continue;
        const n = (c.owner && c.owner.full_name) || null;
        if (!n) continue;
        by[n] = (by[n] || 0) + 1;
      }
      const vals = Object.keys(by).map((k) => by[k]);
      if (vals.length < 2) return none('needs two or more staff with cases');
      const max = Math.max.apply(null, vals);
      const min = Math.min.apply(null, vals);
      const r = safeDiv(max, min);
      if (r === null) return none('needs two or more staff with cases');
      const v = Math.round(r * 100) / 100;
      return { value: v, display: v.toFixed(2) + '×', sub: fmtNum(max) + ' vs ' + fmtNum(min) + ' cases', n: vals.length };
    },
  },
  {
    id: 'legacy_drain',
    label: 'Legacy register migration',
    caveat:
      'Share of the 2024-26 paper-register patients who have LEFT the Legacy queue — i.e. a nurse re-routed them to a real pipeline stage (or lifted the hold). Numerator: legacy patients no longer sitting at triage-on-hold in their bucket. Denominator: every patient carrying a legacy_bucket. Archived (deceased/switched) patients are excluded from both — they are history, not work. 100% means the migration is finished and this tile can retire.',
    unit: '%',
    goodDirection: GOOD.UP,
    target: 100,
    tier: TIER.BOARD,
    section: 'team',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      let total = 0;
      let drained = 0;
      for (const c of (rows && rows.cases) || []) {
        if (!c || !c.legacy_bucket || c.legacy_bucket === 'archived') continue;
        total++;
        const atTriage = resolveStage(c) === 0 && !isClosedCase(c);
        const parked =
          (c.legacy_bucket === 'active' && atTriage) ||
          (c.legacy_bucket === 'inactive' && atTriage && c.program_status === 'on_hold');
        if (!parked) drained++;
      }
      if (!total) return none('no legacy patients loaded');
      const p = pct(drained, total);
      return {
        value: p,
        display: fmtPct(p),
        sub: fmtNum(drained) + ' of ' + fmtNum(total) + ' re-routed',
        n: total,
      };
    },
  },
  {
    id: 'legacy_remaining_by_nurse',
    label: 'Legacy queue remaining, by nurse',
    caveat:
      'Legacy patients still waiting in each nurse’s Legacy queue (active + inactive buckets still parked at triage). This is the migration burn-down per person, not clinical caseload — a nurse with zero here has finished her register, whatever else she still holds.',
    unit: 'patients',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'team',
    chart: 'hbar',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const c of (rows && rows.cases) || []) {
        if (!c || !c.legacy_bucket || c.legacy_bucket === 'archived') continue;
        const atTriage = resolveStage(c) === 0 && !isClosedCase(c);
        const parked =
          (c.legacy_bucket === 'active' && atTriage) ||
          (c.legacy_bucket === 'inactive' && atTriage && c.program_status === 'on_hold');
        if (!parked) continue;
        const n = (c.owner && c.owner.full_name) || 'Unassigned';
        by[n] = (by[n] || 0) + 1;
      }
      const items = Object.keys(by)
        .map((l) => ({ l, v: by[l], c: l === 'Unassigned' ? 'var(--danger-700)' : 'var(--brand-700)' }))
        .sort((a, b) => b.v - a.v);
      const total = items.reduce((a, i) => a + i.v, 0);
      if (!total) return none('legacy queue is clear');
      return { value: total, display: fmtNum(total), sub: 'still to re-route', data: { items } };
    },
  },
  {
    id: 'consents_processed',
    label: 'Consents processed per month (team)',
    caveat:
      'Consent submissions that left the pending state, bucketed by the month they were SUBMITTED (the schema stores no review timestamp, so a consent reviewed months later is credited to its submission month). Team total — consents carry no reviewer, so it cannot be split per coordinator.',
    unit: 'consents/month',
    goodDirection: GOOD.UP,
    tier: TIER.ANALYST,
    section: 'team',
    chart: 'bar',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      /** @type {Record<string, number>} */
      const by = {};
      for (const c of (rows && rows.consents) || []) {
        if (!c || /^pending$/i.test(String(c.status || ''))) continue;
        const k = monthKey(c.created_at);
        if (k) by[k] = (by[k] || 0) + 1;
      }
      const keys = Object.keys(by).sort().slice(-12);
      const series = keys.map((k) => ({ k, l: monthLabel(k), v: by[k] }));
      const total = series.reduce((a, s) => a + s.v, 0);
      const avg = safeDiv(total, series.length);
      return {
        value: avg === null ? null : Math.round(avg),
        display: avg === null ? '—' : fmtNum(Math.round(avg)),
        sub: 'monthly average · ' + fmtNum(total) + ' total',
        data: { series },
      };
    },
  },

  // ═══ Section: Data quality ══════════════════════════════════════════════
  {
    id: 'field_completeness',
    label: 'Field completeness',
    caveat:
      'Share of cases in view carrying each field. Every metric on this page is only as good as these numbers — a 60%-complete county column makes the geography section a sample, not a census.',
    unit: '%',
    goodDirection: GOOD.UP,
    tier: TIER.BOARD,
    section: 'dq',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows) {
      const cases = (rows && rows.cases) || [];
      const n = cases.length;
      const fields = [
        { l: 'Phone number', ok: (c) => !!c.patient_phone },
        { l: 'County', ok: (c) => !!c.county },
        { l: 'Treating facility', ok: (c) => !!(c.treating_facility || c.facility_name) },
        { l: 'Prescriber', ok: (c) => !!c.physician_name },
        { l: 'SHA status recorded', ok: (c) => !!c.sha_status },
        { l: 'Comms consent captured', ok: (c) => {
          const cc = c.comms_consent || {};
          return ['call', 'sms', 'whatsapp', 'email'].some((k) => typeof cc[k] === 'boolean');
        } },
        { l: 'Nurse assigned', ok: (c) => !!c.assigned_to },
        { l: 'Risk bucket set', ok: (c) => !!c.risk_bucket },
      ];
      if (!n) return none('no cases in view');
      const items = fields.map((fd) => {
        const v = pct(cases.filter(fd.ok).length, n) || 0;
        return { l: fd.l, v, c: v >= 95 ? 'var(--ok-700)' : v >= 80 ? 'var(--brand-700)' : v >= 50 ? 'var(--warn-700)' : 'var(--danger-700)' };
      });
      const worst = items.reduce((a, i) => (i.v < a.v ? i : a), items[0]);
      const avg = Math.round(items.reduce((a, i) => a + i.v, 0) / items.length);
      return { value: avg, display: fmtPct(avg), sub: 'weakest: ' + worst.l + ' ' + worst.v + '%', n, data: { items } };
    },
  },
  {
    id: 'hygiene_flags',
    label: 'Hygiene flags',
    caveat:
      'Records needing attention: cases whose normalised phone (last 9 digits) appears on more than one case; active cases with no owning nurse; and active cases whose row has not been touched in 6 months. A shared phone is a POSSIBLE duplicate — families do share numbers — not a confirmed one.',
    unit: 'records',
    goodDirection: GOOD.DOWN,
    tier: TIER.BOARD,
    section: 'dq',
    isNew: true,
    dataState: DATA_STATE.LIVE,
    compute(rows, ctx) {
      const cases = (rows && rows.cases) || [];
      /** @type {Record<string, number>} */
      const phones = {};
      for (const c of cases) {
        const p = String((c && c.patient_phone) || '').replace(/\D/g, '').slice(-9);
        if (p.length >= 9) phones[p] = (phones[p] || 0) + 1;
      }
      let dupes = 0;
      for (const k of Object.keys(phones)) if (phones[k] > 1) dupes += phones[k];
      const act = cases.filter(isActiveCase);
      const unassigned = act.filter((c) => !c.assigned_to).length;
      const now = ctx && ctx.now ? ctx.now : new Date();
      const cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()).getTime();
      const stale = act.filter((c) => {
        if (!c.updated_at) return false;
        const t = new Date(c.updated_at).getTime();
        return !isNaN(t) && t < cutoff;
      }).length;
      const total = dupes + unassigned + stale;
      return {
        value: total,
        display: fmtNum(total),
        sub: total === 1 ? '1 record flagged' : fmtNum(total) + ' records flagged',
        data: {
          rows: [
            { l: 'Cases sharing a phone number', v: dupes, t: 'Cases whose last-9-digit phone appears on more than one case — possible duplicates' },
            { l: 'Unassigned active patients', v: unassigned, t: 'Active cases with no owning nurse' },
            { l: 'Stale records (no update > 6 months)', v: stale, t: 'Active cases whose row has not been touched in 6 months' },
          ],
        },
      };
    },
  },
]);

/** '1st'/'2nd'/'3rd'/'4th'… for a cycle number. @param {number} n @returns {string} */
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

// ─── Registry access (the only way anything reads a metric) ────────────────

/** @type {Record<string, any>} */
const BY_ID = {};
for (const m of METRICS) BY_ID[m.id] = m;

/**
 * A metric definition by id, or undefined. Callers MUST go through this (or
 * metricsFor) rather than reaching into METRICS by index.
 * @param {string} id
 * @returns {any}
 */
export function getMetric(id) {
  return BY_ID[id];
}

/** Every metric id, in registry order. @returns {string[]} */
export function metricIds() {
  return METRICS.map((m) => m.id);
}

/**
 * Filter the registry. `tier: 'board'` returns ONLY board-tier metrics (the
 * curated decision set); `tier: 'analyst'` returns everything, because Analyst
 * is a superset — a board tile is still a tile an analyst wants.
 * @param {{tier?: string, section?: string, kpi?: boolean}} [opts]
 * @returns {any[]}
 */
export function metricsFor(opts) {
  const o = opts || {};
  return METRICS.filter((m) => {
    if (o.section && m.section !== o.section) return false;
    if (o.kpi === true && !m.kpi) return false;
    if (o.kpi === false && m.kpi) return false;
    if (o.tier === TIER.BOARD && m.tier !== TIER.BOARD) return false;
    return true;
  });
}

/** The 12 KPI-band metrics, in order. @returns {any[]} */
export function kpiMetrics() {
  return metricsFor({ kpi: true });
}

/**
 * Compute ONE metric. Never throws: a metric whose compute() blows up on an
 * unexpected row shape degrades to '—' with the error logged, rather than
 * taking the whole page down (a single bad expense row must not blank Reports).
 * @param {string} id
 * @param {any} rows filtered dataset {cases, cycles, consents, aes, expenses, pd}
 * @param {any} [ctx] {now, window, programmes}
 * @returns {MetricResult}
 */
export function computeMetric(id, rows, ctx) {
  const m = BY_ID[id];
  if (!m) return none();
  if (m.dataState === DATA_STATE.NEEDS_FIELD) return needsField();
  try {
    const r = m.compute(rows || {}, ctx || {});
    if (!r || typeof r !== 'object') return none();
    // Belt and braces: a non-finite value must never reach a tile.
    if (r.value !== null && r.value !== undefined && !isFinite(Number(r.value))) {
      return { value: null, display: '—', sub: r.sub || '', data: r.data };
    }
    return r;
  } catch (e) {
    console.warn('metric compute failed:', id, e);
    return none();
  }
}

/**
 * Compute a set of metrics into an id → result map. Passing no ids computes the
 * whole registry (what the CSV export and the deck snapshot do).
 * @param {any} rows
 * @param {any} [ctx]
 * @param {string[]} [ids]
 * @returns {Record<string, MetricResult>}
 */
export function computeAll(rows, ctx, ids) {
  /** @type {Record<string, MetricResult>} */
  const out = {};
  for (const id of ids && ids.length ? ids : metricIds()) out[id] = computeMetric(id, rows, ctx);
  return out;
}

// ─── Delta arithmetic (one definition, used by tiles, CSV and deck) ────────

/**
 * Percentage change of a metric vs its previous-period value, with the honesty
 * rules applied:
 *  - either side missing → null (NO delta is rendered; we do not print "0%" for
 *    "we could not compare");
 *  - the metric is deltaSuppressed → null (the comparison is meaningless);
 *  - previous is 0 and current > 0 → {kind:'new'} (a % change from zero is
 *    infinite, so we say "new" instead of printing ∞ or 100%);
 *  - previous is 0 and current is 0 → null (nothing happened either period).
 * `good` reports whether the movement is in the metric's good direction, so the
 * caller colours it without re-deriving the polarity.
 * @param {string} id
 * @param {number|null|undefined} cur
 * @param {number|null|undefined} prev
 * @returns {{kind: 'pct'|'new', pct: number, good: boolean}|null}
 */
export function metricDelta(id, cur, prev) {
  const m = BY_ID[id];
  if (m && m.deltaSuppressed) return null;
  if (cur === null || cur === undefined || prev === null || prev === undefined) return null;
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p)) return null;
  const dir = (m && m.goodDirection) || GOOD.NEUTRAL;
  if (!p) {
    if (!c) return null;
    return { kind: 'new', pct: 0, good: dir !== GOOD.DOWN };
  }
  const d = Math.round(((c - p) / Math.abs(p)) * 100);
  const good = d === 0 || dir === GOOD.NEUTRAL ? true : d > 0 ? dir === GOOD.UP : dir === GOOD.DOWN;
  return { kind: 'pct', pct: d, good };
}

/**
 * RAG state for a metric against its target: 'ok' | 'warn' | 'danger' | null
 * (null when the metric has no target or no value — never a green light for a
 * number we do not have).
 * @param {string} id
 * @param {number|null|undefined} value
 * @returns {'ok'|'warn'|'danger'|null}
 */
export function metricRag(id, value) {
  const m = BY_ID[id];
  if (!m || typeof m.target !== 'number') return null;
  if (value === null || value === undefined || !isFinite(Number(value))) return null;
  const v = Number(value);
  const t = m.target;
  // Distance from target, as a share of target — 10% inside is amber, worse is red.
  const ratio = safeDiv(v, t);
  if (ratio === null) return null;
  if (m.goodDirection === GOOD.DOWN) {
    if (v <= t) return 'ok';
    return ratio <= 1.1 ? 'warn' : 'danger';
  }
  if (v >= t) return 'ok';
  return ratio >= 0.9 ? 'warn' : 'danger';
}
